import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import TripSheetModal from './TripSheetModal';
import { formatCurrency, getCurrencySymbol } from '../../utils/formatters';

const API = '/api';

const Icon = ({ d, size = 16, strokeWidth = 1.75 }) => (
    <svg
        width={size} height={size} viewBox="0 0 24 24"
        fill="none" stroke="currentColor"
        strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round"
    >
        {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
    </svg>
);

const icons = {
    search: ['M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16z', 'M21 21l-4.35-4.35'],
    plus: ['M12 5v14', 'M5 12h14'],
    edit: ['M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7', 'M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z'],
    trash: ['M3 6h18', 'M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2'],
    x: ['M18 6 6 18', 'M6 6l12 12'],
    map: ['M1 6v16l7-4 8 4 7-4V2l-7 4-8-4-7 4z', 'M8 2v16', 'M16 6v16'],
    upload: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4 M17 8l-5-5-5 5 M12 3v12',
    download: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4 M7 10l5 5 5-5 M12 15V3',
    truck: ['M1 3h15v13H1z', 'M16 8h4l3 3v5h-7V8z', 'M5.5 21a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z', 'M18.5 21a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z'],
    user: ['M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2', 'M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z'],
    check: ['M20 6L9 17l-5-5'],
    calendar: ['M8 2v4', 'M16 2v4', 'M3 10h18', 'M21 8a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V8z'],
    car: ['M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.5 2.5C1.4 11.3 1 12.1 1 13v3c0 .6.4 1 1 1h2', 'M7 17a2 2 0 1 0 4 0 2 2 0 0 0-4 0', 'M15 17a2 2 0 1 0 4 0 2 2 0 0 0-4 0'],
    clock: ['M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z', 'M12 6v6l4 2'],
};

/* ── Availability Panel ── */
function AvailabilityPanel({ onClose, vehicles, drivers, vendorFleet, trips }) {
    const [tab, setTab] = useState('fleet'); // fleet | drivers | vfleet | vdrivers
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const HOURS = Array.from({ length: 24 }, (_, i) => i);

    const COLORS = {
        Available: { bg: 'linear-gradient(135deg,#4ade80,#22c55e)', border: '#86efac', shadow: 'rgba(34,197,94,.2)', text: '#15803d' },
        Daily: { bg: 'linear-gradient(135deg,#fb923c,#f97316)', border: '#fdba74', shadow: 'rgba(249,115,22,.25)', text: '#ea580c' },
        Monthly: { bg: 'linear-gradient(135deg,#a78bfa,#7c3aed)', border: '#c4b5fd', shadow: 'rgba(124,58,237,.25)', text: '#7c3aed' },
        Shuttle: { bg: 'linear-gradient(135deg,#60a5fa,#3b82f6)', border: '#93c5fd', shadow: 'rgba(59,130,246,.25)', text: '#1d4ed8' },
        'Shuttle with Booking': { bg: 'linear-gradient(135deg,#22d3ee,#06b6d4)', border: '#67e8f9', shadow: 'rgba(6,182,212,.25)', text: '#0891b2' },
        'Ad-hoc': { bg: 'linear-gradient(135deg,#fcd34d,#f59e0b)', border: '#fde68a', shadow: 'rgba(245,158,11,.25)', text: '#b45309' },
        Absent: { bg: 'linear-gradient(135deg,#f87171,#ef4444)', border: '#fca5a5', shadow: 'rgba(239,68,68,.25)', text: '#b91c1c' },
    };

    const getLeaveAtDate = (entity) => {
        if (!entity || !entity.leaves) return null;
        const target = new Date(); // In the modal we usually look at 'Today'
        return entity.leaves.find(l => {
            if (l.status !== 'Approved' && l.status !== 'Active') return false;
            const start = new Date(l.startDate);
            const end = new Date(l.endDate);
            return target >= start && target <= end;
        });
    };

    const getTripAtHour = (entityId, hour) => {
        const activeTrips = trips.filter(t => {
            const vid = t.vehicleId?._id || t.vehicleId;
            const did = t.driverId?._id || t.driverId;
            const vfid = t.vendorProposedVehicleId?._id || t.vendorProposedVehicleId;
            return (vid === entityId || did === entityId || vfid === entityId) &&
                ['Scheduled', 'Ongoing'].includes(t.status);
        });
        return activeTrips.find(t => {
            if (t.billingType === 'Monthly') return true;
            if (!t.startTime) return false;
            const [sh] = t.startTime.split(':').map(Number);
            const [eh] = (t.endTime || t.startTime).split(':').map(Number);
            return hour >= sh && hour <= (eh || sh);
        });
    };

    const generalDrivers = drivers.filter(d => !d.vendorId);
    const vendorDrivers = drivers.filter(d => d.vendorId);

    const TABS = [
        { key: 'fleet', label: 'General Fleet', count: vehicles.length, icon: icons.car },
        { key: 'drivers', label: 'General Drivers', count: generalDrivers.length, icon: icons.user },
        { key: 'vfleet', label: 'Vendor Fleet', count: vendorFleet.length, icon: icons.truck },
        { key: 'vdrivers', label: 'Vendor Drivers', count: vendorDrivers.length, icon: icons.user },
    ];

    const allRows = (() => {
        const q = search.toLowerCase();
        if (tab === 'fleet') return vehicles.filter(v => (v.registrationNumber + (v.make || '')).toLowerCase().includes(q)).map(v => ({ id: v._id, name: v.registrationNumber, sub: v.make + ' ' + (v.model || ''), icon: icons.car, raw: v }));
        if (tab === 'drivers') return generalDrivers.filter(d => d.name?.toLowerCase().includes(q)).map(d => ({ id: d._id, name: d.name, sub: d.phoneNumber || '', icon: icons.user, raw: d }));
        if (tab === 'vfleet') return vendorFleet.filter(f => (f.registrationNumber || '').toLowerCase().includes(q)).map(f => ({ id: f._id, name: f.registrationNumber, sub: f.vehicleType || f.make || '', icon: icons.truck, raw: f }));
        if (tab === 'vdrivers') return vendorDrivers.filter(d => d.name?.toLowerCase().includes(q)).map(d => ({ id: d._id, name: d.name, sub: d.phoneNumber || '', icon: icons.user, raw: d }));
        return [];
    })();

    const currentHour = new Date().getHours();

    const rows = allRows.filter(r => {
        if (statusFilter === 'all') return true;
        if (statusFilter === 'Available') return !getTripAtHour(r.id, currentHour) && !getLeaveAtDate(r.raw);
        if (statusFilter === 'Absent') return !!getLeaveAtDate(r.raw);
        if (statusFilter === 'On Trip') return !!getTripAtHour(r.id, currentHour);
        
        // Specific billing type filter
        return HOURS.some(h => {
            const t = getTripAtHour(r.id, h);
            return t && t.billingType === statusFilter;
        });
    });

    const availCount = allRows.filter(r => !getTripAtHour(r.id, currentHour) && !getLeaveAtDate(r.raw)).length;
    const onTripCount = allRows.filter(r => !!getTripAtHour(r.id, currentHour)).length;

    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
            <div style={{ background: '#fff', borderRadius: 20, width: '92%', maxWidth: 900, maxHeight: '88vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 25px 60px rgba(0,0,0,0.25)' }} onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div style={{ padding: '18px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#063A5C' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <Icon d={icons.calendar} size={18} strokeWidth={2} />
                        <span style={{ color: 'white', fontWeight: 800, fontSize: 16 }}>Availability Overview</span>
                        <span style={{ background: 'rgba(255,255,255,0.15)', color: 'white', fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20 }}>Today</span>
                    </div>
                    <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: 'white', borderRadius: 8, width: 30, height: 30, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Icon d={icons.x} size={16} />
                    </button>
                </div>

                {/* Tabs */}
                <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid #f1f5f9', background: '#f8fafc' }}>
                    {TABS.map(t => (
                        <button key={t.key} onClick={() => { setTab(t.key); setSearch(''); }}
                            style={{ flex: 1, padding: '12px 8px', border: 'none', cursor: 'pointer', background: tab === t.key ? '#fff' : 'transparent', borderBottom: tab === t.key ? '2px solid #16539C' : '2px solid transparent', fontWeight: tab === t.key ? 800 : 600, color: tab === t.key ? '#16539C' : '#64748b', fontSize: 13, transition: 'all 0.15s' }}>
                            {t.label}
                            <span style={{ marginLeft: 6, background: tab === t.key ? '#e6f0fa' : '#f1f5f9', color: tab === t.key ? '#16539C' : '#94a3b8', borderRadius: 20, padding: '1px 7px', fontSize: 11, fontWeight: 700 }}>{t.count}</span>
                        </button>
                    ))}
                </div>

                {/* Summary Cards */}
                <div style={{ display: 'flex', gap: 12, padding: '14px 20px 0', background: '#fff' }}>
                    {[
                        { label: 'Total', val: allRows.length, bg: '#eff6ff', color: '#1d4ed8', key: 'all' },
                        { label: 'Available Now', val: availCount, bg: '#f0fdf4', color: '#166534', key: 'Available' },
                        { label: 'On Trips Now', val: onTripCount, bg: 'rgba(249,115,22,.15)', color: '#ea580c', key: 'On Trip' },
                        { label: 'Absent Today', val: allRows.filter(r => getLeaveAtDate(r.raw)).length, bg: 'rgba(239,68,68,.15)', color: '#b91c1c', key: 'Absent' },
                    ].map(s => (
                        <div 
                            key={s.label} 
                            onClick={() => setStatusFilter(s.key)}
                            style={{ 
                                flex: 1, background: s.bg, borderRadius: 12, padding: '10px 16px', textAlign: 'center', cursor: 'pointer',
                                transition: 'all 0.2s', transform: statusFilter === s.key ? 'scale(1.05)' : 'none',
                                boxShadow: statusFilter === s.key ? '0 4px 12px rgba(0,0,0,0.1)' : 'none',
                                border: statusFilter === s.key ? `2px solid ${s.color}` : '2px solid transparent'
                            }}
                        >
                            <div style={{ fontSize: 26, fontWeight: 900, color: s.color }}>{s.val}</div>
                            <div style={{ fontSize: 11, fontWeight: 700, color: s.color, opacity: 0.75 }}>{s.label}</div>
                        </div>
                    ))}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '0 8px', flexWrap: 'wrap', maxWidth: 320, justifyContent: 'flex-end' }}>
                        {Object.entries(COLORS).map(([l, c]) => (
                            <div 
                                key={l} 
                                onClick={() => setStatusFilter(l)}
                                style={{ 
                                    display: 'flex', alignItems: 'center', gap: 5, fontSize: 10, fontWeight: 700, 
                                    color: statusFilter === l ? '#000' : '#374151', cursor: 'pointer',
                                    background: statusFilter === l ? '#f1f5f9' : 'transparent',
                                    padding: '2px 6px', borderRadius: 6
                                }}
                            >
                                <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 3, background: c.bg.split(',')[1].replace(')', '') }} /> 
                                {l.split(' ')[0]}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Search */}
                <div style={{ padding: '10px 20px', background: '#fff' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#f8fafc', borderRadius: 10, border: '1px solid #e2e8f0', padding: '0 12px' }}>
                        <Icon d={icons.search} size={13} />
                        <input value={search} onChange={e => setSearch(e.target.value)} placeholder={`Search ${TABS.find(t => t.key === tab)?.label}...`}
                            style={{ flex: 1, border: 'none', outline: 'none', padding: '9px 0', fontSize: 13, background: 'transparent', fontFamily: 'inherit' }} />
                    </div>
                </div>

                {/* Grid */}
                <div style={{ flex: 1, overflowY: 'auto', overflowX: 'auto', padding: '0 20px 20px' }}>
                    {rows.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '50px 20px', color: '#94a3b8', fontSize: 13 }}>No records found</div>
                    ) : (
                        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 800 }}>
                            <thead>
                                <tr>
                                    <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: 11, fontWeight: 700, color: '#94a3b8', whiteSpace: 'nowrap', minWidth: 160 }}>Name</th>
                                    <th style={{ textAlign: 'left', padding: '8px 8px', fontSize: 11, fontWeight: 700, color: '#94a3b8', whiteSpace: 'nowrap', width: 80 }}>Hours</th>
                                    {HOURS.map(h => (
                                        <th key={h} style={{ padding: '4px 2px', fontSize: 9, fontWeight: 700, color: '#94a3b8', textAlign: 'center', width: 26 }}>
                                            {h === 0 ? '12' : h > 12 ? h - 12 : h}
                                            <div style={{ fontSize: 8, color: '#cbd5e1' }}>{h < 12 ? 'AM' : 'PM'}</div>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map(r => {
                                    const leave = getLeaveAtDate(r.raw);
                                    const busyHours = HOURS.map(h => getTripAtHour(r.id, h)).filter(Boolean);
                                    const isAvail = !leave && busyHours.length === 0;
                                    const isMonthly = busyHours.some(t => t.billingType === 'Monthly');
                                    
                                    const statusKey = leave ? 'Absent' : (isMonthly ? 'Monthly' : (isAvail ? 'Available' : 'Daily'));
                                    const sc = COLORS[statusKey];

                                    return (
                                        <tr key={r.id} style={{ borderBottom: '1px solid #f8fafc' }}>
                                            <td style={{ padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap' }}>
                                                <div style={{ 
                                                    width: 30, height: 30, borderRadius: 8, 
                                                    background: sc.bg.split(',')[1].replace(')', '') + '15', 
                                                    color: sc.text, display: 'flex', alignItems: 'center', 
                                                    justifyContent: 'center', flexShrink: 0 
                                                }}>
                                                    <Icon d={r.icon} size={15} color={sc.text} strokeWidth={2.2} />
                                                </div>
                                                <div>
                                                    <div style={{ fontWeight: 700, fontSize: 12, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 4 }}>
                                                        {r.name}
                                                        {isMonthly && <span style={{ fontSize: 8, background: COLORS.Monthly.text, color: 'white', padding: '0 4px', borderRadius: 3 }}>M</span>}
                                                        {leave && <span style={{ fontSize: 8, background: COLORS.Absent.text, color: 'white', padding: '0 4px', borderRadius: 3 }}>ABSENT</span>}
                                                    </div>
                                                    <div style={{ fontSize: 10, color: '#94a3b8' }}>{r.sub}</div>
                                                </div>
                                            </td>
                                            <td style={{ padding: '8px', fontSize: 11, color: sc.text, whiteSpace: 'nowrap', fontWeight: 700 }}>{leave ? (leave.leaveType || 'Leave') : (isMonthly ? 'Dedicated' : (busyHours.length > 0 ? `${busyHours.length}h busy` : '0h busy'))}</td>
                                            {HOURS.map(h => {
                                                const trip = getTripAtHour(r.id, h);
                                                const busy = !!trip;
                                                const type = trip?.billingType || (leave ? 'Absent' : 'Available');
                                                const style = COLORS[type] || COLORS.Daily;
                                                const tooltip = leave ? `Absent: ${leave.leaveType}` : (busy ? `${trip.clientId?.companyName || 'Trip'} (${type})` : 'Available');
                                                return (
                                                    <td key={h} style={{ padding: '3px 2px' }}>
                                                        <div title={tooltip} style={{ width: 22, height: 22, borderRadius: 4, background: style.bg, border: `1.5px solid ${style.border}`, cursor: 'default' }} />
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
}

export default function Trips({ user, onNavigate, operatorDetails }) {
    const [trips, setTrips] = useState([]);
    const [instances, setInstances] = useState([]);
    const [routes, setRoutes] = useState([]);
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const todayStr = new Date().toISOString().split('T')[0];
    const [filters, setFilters] = useState({ date: todayStr, client: '', route: '', billingType: '', safetyOnly: false });
    const [modal, setModal] = useState(null);
    const [deleteModal, setDeleteModal] = useState(null);
    const [vehicles, setVehicles] = useState([]);
    const [drivers, setDrivers] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [vendorFleet, setVendorFleet] = useState([]);
    const [allocations, setAllocations] = useState([]);
    const [importing, setImporting] = useState(false);
    const [approvalModal, setApprovalModal] = useState(null);
    const [passengerListModal, setPassengerListModal] = useState(null);
    const [mappings, setMappings] = useState([]);
    const [showImportScreen, setShowImportScreen] = useState(false);
    const [tripSheetModal, setTripSheetModal] = useState(null);

    const getInstanceForTrip = (tripId, date) => {
        if (!instances || !Array.isArray(instances)) return null;
        const targetDate = date || new Date().toISOString().split('T')[0];
        return instances.find(inst => {
            const mId = inst.masterTripId?._id || inst.masterTripId;
            const iDate = inst.tripDate ? String(inst.tripDate) : null;
            return mId === tripId && iDate && iDate.startsWith(targetDate);
        });
    };

    const handleUpdateInstanceStatus = async (trip, status) => {
        const targetDate = filters.date || new Date().toISOString().split('T')[0];
        try {
            await axios.post(`${API}/trip-instances`, {
                masterTripId: trip._id,
                operatorId,
                tripDate: targetDate,
                status,
                vehicleId: trip.vehicleId?._id || trip.vehicleId,
                driverId: trip.driverId?._id || trip.driverId
            });

            if (trip.tripFrequency !== 'Recurring') {
                await axios.put(`${API}/trips/${trip._id}`, { status });
            }

            fetchData();
        } catch (err) {
            console.error(err);
            alert('Failed to update trip status');
        }
    };
    const fileInputRef = useRef(null);

    const operatorId = user?.operatorId || user?._id || 'all';

    useEffect(() => {
        fetchData();
        fetchCommonData();
    }, []);

    const fetchCommonData = async () => {
        try {
            const [clientRes, routeRes, vehicleRes, driverRes, vendorRes, vfRes, allocRes, instRes, mappingsRes] = await Promise.all([
                axios.get(`${API}/clients/${operatorId}`),
                axios.get(`${API}/routes/${operatorId}`),
                axios.get(`${API}/vehicles/${operatorId}`),
                axios.get(`${API}/drivers/${operatorId}`),
                axios.get(`${API}/vendors/${operatorId}`),
                axios.get(`${API}/vendor-fleet/${operatorId}`),
                axios.get(`${API}/passenger-allocations/${operatorId}`),
                axios.get(`${API}/trip-instances/${operatorId}`),
                axios.get(`${API}/driver-vehicle-mappings/${operatorId}`)
            ]);
            setClients(clientRes.data);
            setRoutes(routeRes.data);
            setVehicles(vehicleRes.data);
            setDrivers(driverRes.data);
            setVendors(vendorRes.data);
            setVendorFleet(vfRes.data);
            setAllocations(allocRes.data);
            setInstances(instRes.data);
            setMappings(mappingsRes.data || []);
        } catch (err) { console.error(err); }
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            const [tripRes, allocRes, instRes] = await Promise.all([
                axios.get(`${API}/trips/${operatorId}`),
                axios.get(`${API}/passenger-allocations/${operatorId}`),
                axios.get(`${API}/trip-instances/${operatorId}`)
            ]);
            setTrips(tripRes.data);
            setAllocations(allocRes.data);
            setInstances(instRes.data);
        } catch (err) { console.error(err); }
        finally { setLoading(false); }
    };

    const downloadTemplate = () => {
        const templateData = [
            {
                'Client': 'Infosys Ltd',
                'Route': 'IT Park Shuttle',
                'Trip Type': 'Login',
                'Frequency': 'Recurring',
                'Recurring Days': 'Mon,Tue,Wed,Thu,Fri',
                'Trip Date': '',
                'Start Time': '08:00',
                'End Time': '09:00',
                'Vehicle Plate': 'DXB-1234',
                'Driver Name': 'John Doe',
                'Amount': 500,
                'Billing Category': 'Daily',
                'Status': 'Scheduled'
            }
        ];
        const ws = XLSX.utils.json_to_sheet(templateData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Trips');
        XLSX.writeFile(wb, 'Trips_Template.xlsx');
    };

    const handleImport = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setImporting(true);
        const reader = new FileReader();
        reader.onload = async (evt) => {
            try {
                const bstr = evt.target.result;
                const wb = XLSX.read(bstr, { type: 'binary' });
                const ws = wb.Sheets[wb.SheetNames[0]];
                const data = XLSX.utils.sheet_to_json(ws);

                const mapped = data.map(row => ({
                    client: row['Client'],
                    route: row['Route'],
                    tripType: row['Trip Type'] || 'Login',
                    tripFrequency: row['Frequency'] || 'One Time',
                    recurringDays: row['Recurring Days'] ? row['Recurring Days'].split(',').map(d => d.trim()) : [],
                    tripDate: row['Trip Date'],
                    startTime: row['Start Time'],
                    endTime: row['End Time'],
                    vehiclePlate: row['Vehicle Plate'],
                    driverName: row['Driver Name'],
                    amount: row['Amount'],
                    billingType: row['Billing Category'] || 'Daily',
                    status: row['Status'] || 'Scheduled'
                }));

                const res = await axios.post(`${API}/trips/import`, { operatorId, trips: mapped });
                if (res.status === 201) {
                    alert(`${res.data.successCount} trips imported!`);
                    fetchData();
                }
            } catch (err) {
                alert('Import failed. Please check your data format.');
            } finally {
                setImporting(false);
                e.target.value = '';
            }
        };
        reader.readAsBinaryString(file);
    };

    const handleApprove = async (trip, extra) => {
        try {
            let rid = trip.routeId?._id || trip.routeId;
            const updatePayload = {
                ...trip,
                proposedPrice: extra.amount,
                vehicleId: trip.requestedVehicleId?._id || trip.requestedVehicleId || trip.vehicleId,
                status: 'PriceProposed'
            };
            
            if (!rid) {
                // Defer route creation until the client accepts the proposal
                updatePayload.tripName = extra.routeName;
                updatePayload.totalDistance = extra.distance || 0;
            } else {
                updatePayload.routeId = rid;
            }
            
            await axios.put(`${API}/trips/${trip._id}`, updatePayload);
            alert('Price proposal sent to client successfully!');
            setApprovalModal(null);
            fetchData();
        } catch (err) {
            console.error('Error approving trip:', err);
            alert('Failed to approve trip request.');
        }
    };

    const acceptVendorProposal = async (trip) => {
        try {
            await axios.put(`${API}/trips/${trip._id}`, {
                vendorStatus: 'Accepted',
                vehicleId: trip.vendorProposedVehicleId?._id || trip.vendorProposedVehicleId,
                driverId: trip.vendorProposedDriverId?._id || trip.vendorProposedDriverId,
                status: 'Scheduled'
            });
            fetchData();
        } catch (err) { console.error(err); alert('Failed to accept proposal'); }
    };

    const rejectVendorProposal = async (trip) => {
        try {
            await axios.put(`${API}/trips/${trip._id}`, { vendorStatus: 'Rejected' });
            fetchData();
        } catch (err) { console.error(err); alert('Failed to reject proposal'); }
    };

    const handleSave = async (formData) => {
        try {
            if (modal.data?._id && !modal.isDispatchOnly && modal.data?.vehicleId && !formData.changeReason) {
                alert("Please provide a status or reason for changing this trip schedule.");
                return;
            }
            // Clean payload
            const payload = { ...formData, operatorId, operatorName: operatorDetails?.companyName || 'Operator Admin' };

            // Ensure all ID fields are either a valid ID or null/removed (Mongoose fails on empty strings)
            const sanitizeId = (id) => {
                if (!id) return null;
                if (typeof id === 'object') return id._id || null;
                return id === '' ? null : id;
            };

            payload.clientId = sanitizeId(payload.clientId);
            payload.routeId = sanitizeId(payload.routeId);
            payload.vehicleId = sanitizeId(payload.vehicleId);
            payload.driverId = sanitizeId(payload.driverId);
            payload.vendorId = sanitizeId(payload.vendorId);

            // Clean up tripDate (Mongoose rejects empty strings for Date fields)
            if (!payload.tripDate || payload.tripFrequency === 'Recurring') {
                payload.tripDate = null;
            }
            if (!payload.startDate) payload.startDate = null;
            if (!payload.endDate) payload.endDate = null;

            // Remove Mongoose-specific meta fields if they exist
            delete payload.__v;
            delete payload.createdAt;
            delete payload.updatedAt;

            // If finalizing a client-accepted trip, move to Scheduled
            if (payload.status === 'ClientAccepted') {
                payload.status = 'Scheduled';
            }

            if (modal.data?._id) {
                await axios.put(`${API}/trips/${modal.data._id}`, payload);
                alert('Trip schedule updated successfully!');
            } else {
                // Remove _id if it somehow leaked into new form state
                delete payload._id;
                await axios.post(`${API}/trips`, payload);
                alert('Trip schedule created successfully!');
            }
            setModal(null);
            fetchData();
        } catch (err) {
            console.error('Error saving trip:', err);
            alert(`Failed to save trip: ${err.response?.data?.message || err.message}`);
        }
    };

    const handleDelete = async () => {
        try {
            await axios.delete(`${API}/trips/${deleteModal._id}`);
            setDeleteModal(null);
            fetchData();
        } catch (err) {
            console.error(err);
            alert('Error deleting trip');
        }
    };

    const filtered = trips.filter(t => {
        const matchesSearch = (
            t.routeId?.name?.toLowerCase().includes(search.toLowerCase()) ||
            t.clientId?.companyName?.toLowerCase().includes(search.toLowerCase()) ||
            t.vendorId?.vendorName?.toLowerCase().includes(search.toLowerCase()) ||
            t.passengerName?.toLowerCase().includes(search.toLowerCase()) ||
            t.passengerPhone?.includes(search)
        );
        const tripAllocations = allocations.filter(a => (a.tripId?._id || a.tripId) === t._id);
        const femaleCount = tripAllocations.filter(a => a.passengerId?.gender === 'Female').length;
        const startTimeHour = t.startTime ? parseInt(t.startTime.split(':')[0]) : -1;
        const isNightTrip = startTimeHour >= 18 || startTimeHour < 5;
        const safetyRisk = isNightTrip && femaleCount > 0;

        return matchesSearch && 
               (!filters.client || t.clientId?._id === filters.client || t.clientId === filters.client) &&
               (!filters.route || t.routeId?._id === filters.route || t.routeId === filters.route) &&
               (!filters.billingType || t.billingType === filters.billingType) &&
               (!filters.date || (t.tripFrequency === 'Recurring' ? (
                   (t.recurringDays?.includes(new Date(filters.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short' })) && (!t.tripDate || String(t.tripDate).split('T')[0] <= filters.date)) || 
                   !!getInstanceForTrip(t._id, filters.date)
               ) : (t.tripDate && t.tripDate.startsWith(filters.date)))) &&
               (!filters.safetyOnly || safetyRisk);
    });

    if (showImportScreen) {
        return (
            <ImportWizard
                trips={trips}
                operatorId={operatorId}
                onBack={() => setShowImportScreen(false)}
                onDone={() => { setShowImportScreen(false); fetchData(); }}
            />
        );
    }

    return (
        <div className="md-page">
            <div className="md-header">
                <div>
                    <h1 className="md-title">Trips Management</h1>
                    <p className="md-subtitle">Schedule and optimize vehicle trips for clients.</p>
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                    <button className="premium-btn btn-import" onClick={() => setShowImportScreen(true)}>
                        <Icon d={icons.upload} size={18} strokeWidth={2.5} />
                        Import
                    </button>
                    <button className="premium-btn btn-template" onClick={downloadTemplate}>
                        <Icon d={icons.download} size={18} strokeWidth={2.5} />
                        Template
                    </button>
                    <button className="premium-btn btn-primary-action" onClick={() => setModal({ data: null })}>
                        <Icon d={icons.plus} size={18} strokeWidth={2.5} />
                        Add Trip
                    </button>
                </div>
            </div>

            {/* Pending Requests Notification Bar */}
            {trips.filter(t => t.status === 'Requested').length > 0 && (
                <div style={{
                    marginBottom: 24, padding: '16px 24px', borderRadius: 16,
                    background: 'linear-gradient(90deg, #063A5C 0%, #312e81 100%)',
                    color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    boxShadow: '0 10px 20px -5px rgba(6, 58, 92, 0.3)', animation: 'slideIn 0.5s ease'
                }}>
                    <style>{`@keyframes slideIn { from { transform: translateY(-20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }`}</style>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 15 }}>
                        <div style={{ width: 42, height: 42, borderRadius: '50%', background: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Icon d={icons.map} size={20} />
                        </div>
                        <div>
                            <div style={{ fontSize: 16, fontWeight: 800 }}>{trips.filter(t => t.status === 'Requested').length} Pending Requests</div>
                            <div style={{ fontSize: 13, opacity: 0.8, fontWeight: 500 }}>Clients have submitted new trip requirements awaiting your approval.</div>
                        </div>
                    </div>
                    <button
                        onClick={() => {
                            const firstReq = trips.find(t => t.status === 'Requested');
                            setApprovalModal(firstReq);
                        }}
                        style={{ padding: '10px 20px', borderRadius: 12, border: 'none', background: 'white', color: '#063A5C', fontWeight: 800, fontSize: 13, cursor: 'pointer' }}
                    >
                        Review Latest Request
                    </button>
                </div>
            )}

            {/* Vendor Proposal Alert Banner */}
            {trips.filter(t => t.vendorStatus === 'Vendor Proposed').length > 0 && (
                <div style={{
                    marginBottom: 24, padding: '16px 24px', borderRadius: 16,
                    background: 'linear-gradient(90deg, #065f46 0%, #059669 100%)',
                    color: 'white', display: 'flex', alignItems: 'center', gap: 15,
                    boxShadow: '0 10px 20px -5px rgba(6,95,70,0.35)'
                }}>
                    <div style={{ width: 42, height: 42, borderRadius: '50%', background: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Icon d={icons.truck} size={22} />
                    </div>
                    <div>
                        <div style={{ fontSize: 16, fontWeight: 800 }}>
                            {trips.filter(t => t.vendorStatus === 'Vendor Proposed').length} Vendor Proposal{trips.filter(t => t.vendorStatus === 'Vendor Proposed').length > 1 ? 's' : ''} Awaiting Your Approval
                        </div>
                        <div style={{ fontSize: 13, opacity: 0.85, fontWeight: 500 }}>
                            Vendor(s) have submitted vehicle, driver and pricing — review and accept in the Vehicle & Driver column below.
                        </div>
                    </div>
                </div>
            )}

            <div className="md-panel">

                <div className="md-panel-toolbar" style={{ flexWrap: 'wrap', gap: '15px' }}>
                    <div className="md-search-wrap" style={{ minWidth: '250px' }}>
                        <Icon d={icons.search} size={14} />
                        <input
                            className="md-input md-search"
                            placeholder="Search trips..."
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                        />
                    </div>
                    <div style={{ display: 'flex', gap: '10px', flex: 1 }}>
                        <select className="md-input" value={filters.client} onChange={e => setFilters({ ...filters, client: e.target.value })} style={{ height: '40px' }}>
                            <option value="">All Clients</option>
                            {clients.map(c => <option key={c._id} value={c._id}>{c.companyName}</option>)}
                        </select>
                        <select className="md-input" value={filters.billingType} onChange={e => setFilters({ ...filters, billingType: e.target.value })} style={{ height: '40px' }}>
                            <option value="">All Billing</option>
                            <option value="Ad-hoc">Adhoc</option>
                            <option value="Daily">Daily</option>
                            <option value="Monthly">Monthly</option>
                            <option value="Shuttle">Shuttle</option>
                            <option value="Shuttle with Booking">Shuttle with Booking</option>
                        </select>
                        <select className="md-input" value={filters.route} onChange={e => setFilters({ ...filters, route: e.target.value })} style={{ height: '40px' }}>
                            <option value="">All Routes</option>
                            {routes.map(r => <option key={r._id} value={r._id}>{r.name}</option>)}
                        </select>
                        <input type="date" className="md-input" value={filters.date} onChange={e => setFilters({ ...filters, date: e.target.value })} style={{ height: '40px' }} />
                            <label style={{ 
                                display: 'flex', alignItems: 'center', gap: 8, padding: '0 15px', 
                                borderRadius: 10, border: '1px solid #e2e8f0', background: filters.safetyOnly ? '#fce7f3' : '#f8fafc',
                                cursor: 'pointer', userSelect: 'none', transition: 'all 0.2s', minWidth: '130px',
                                borderColor: filters.safetyOnly ? '#C93269' : '#e2e8f0'
                            }}>
                            <input 
                                type="checkbox" 
                                checked={filters.safetyOnly} 
                                onChange={e => setFilters({ ...filters, safetyOnly: e.target.checked })}
                                style={{ cursor: 'pointer', width: 16, height: 16 }}
                            />
                            <span style={{ fontSize: 13, fontWeight: 700, color: filters.safetyOnly ? '#be185d' : '#64748b' }}>Safety Only</span>
                        </label>
                    </div>
                </div>

                {loading ? (
                    <div style={{ padding: 60, textAlign: 'center', color: '#94a3b8' }}>Loading trips...</div>
                ) : (
                    <div className="md-table-wrap">
                        <table className="md-table">
                            <thead>
                                <tr>
                                    <th className="md-th">Client</th>
                                    <th className="md-th">Route Details</th>
                                    <th className="md-th">KM (Mileage)</th>
                                    <th className="md-th">Passengers</th>
                                    <th className="md-th">Vehicle & Driver</th>
                                    <th className="md-th">Schedule</th>
                                    <th className="md-th">Trip Fee</th>
                                    <th className="md-th">Status</th>
                                    <th className="md-th">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map(t => {
                                    const tripAllocations = allocations.filter(a => (a.tripId?._id || a.tripId) === t._id);
                                    const femaleCount = tripAllocations.filter(a => a.passengerId?.gender === 'Female').length;
                                    const startTimeHour = t.startTime ? parseInt(t.startTime.split(':')[0]) : -1;
                                    const isNightTrip = startTimeHour >= 18 || startTimeHour < 5;
                                    const safetyRisk = isNightTrip && femaleCount > 0;
                                    const routeObj = routes.find(r => r._id === (t.routeId?._id || t.routeId));
                                    const displayDistance = routeObj?.distance || t.routeId?.distance || t.totalDistance || t.distance || 0;
                                    return (
                                        <tr key={t._id} style={{ background: safetyRisk ? '#fce7f3' : 'transparent' }}>
                                        <td className="md-td" style={{ position: 'relative', overflow: 'hidden' }}>
                                            {safetyRisk && (
                                                <div style={{
                                                    position: 'absolute', top: 0, left: 0, width: 45, height: 45,
                                                    overflow: 'hidden', zIndex: 2, pointerEvents: 'none'
                                                }}>
                                                    <div style={{
                                                        position: 'absolute', top: 6, left: -14, width: 60,
                                                        background: '#C93269', color: 'white', fontSize: 7, fontWeight: 900,
                                                        textAlign: 'center', transform: 'rotate(-45deg)',
                                                        boxShadow: '0 2px 5px rgba(0,0,0,0.2)', textTransform: 'uppercase',
                                                        letterSpacing: '0.05em', padding: '1px 0'
                                                    }}>
                                                        SAFETY
                                                    </div>
                                                </div>
                                            )}
                                            {t.tripType === 'on_demand' ? (
                                                <div>
                                                    <div style={{ color: '#16539C', fontSize: 11, fontWeight: 800, textTransform: 'uppercase', marginBottom: 2 }}>{t.passengerName || 'Adhoc Guest'}</div>
                                                    <div style={{ fontWeight: 700, color: '#063A5C' }}>{t.clientId?.companyName || '—'}</div>
                                                    <div style={{ fontSize: 10, color: '#94a3b8' }}>ID: {t._id.slice(-6).toUpperCase()}{t.passengerPhone ? ` • ${t.passengerPhone}` : ''}</div>
                                                </div>
                                            ) : (
                                                <div>
                                                    {t.tripName && <div style={{ color: '#11427D', fontSize: 11, fontWeight: 800, textTransform: 'uppercase', marginBottom: 2 }}>{t.tripName}</div>}
                                                    <div style={{ fontWeight: 700, color: '#063A5C' }}>{t.clientId?.companyName || '—'}</div>
                                                    <div style={{ fontSize: 10, color: '#94a3b8' }}>ID: {t._id.slice(-6).toUpperCase()}</div>
                                                </div>
                                            )}
                                        </td>
                                        <td className="md-td">
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                <div style={{
                                                    padding: '4px 8px', borderRadius: 6,
                                                    background: t.billingType?.includes('Shuttle') ? '#f0fdf4' : (t.billingType === 'Ad-hoc' ? '#fef2f2' : '#f1f5f9'),
                                                    color: t.billingType?.includes('Shuttle') ? '#166534' : (t.billingType === 'Ad-hoc' ? '#991b1b' : '#64748b'),
                                                    fontSize: 10, fontWeight: 700
                                                }}>
                                                    {t.billingType || 'Daily'}
                                                </div>
                                                <div style={{
                                                    padding: '4px 8px', borderRadius: 6,
                                                    background: t.tripType === 'on_demand' ? '#e6f0fa' : '#f1f5f9',
                                                    color: t.tripType === 'on_demand' ? '#11427D' : '#64748b',
                                                    fontSize: 10, fontWeight: 700
                                                }}>
                                                    {t.tripType === 'on_demand' ? 'Adhoc' : (t.tripType || 'Fixed')}
                                                </div>
                                                <strong>{t.tripType === 'on_demand' ? 'Adhoc Trip' : (t.routeId?.name || '—')}</strong>
                                            </div>
                                            <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
                                                {t.tripType === 'on_demand' ? `${t.pickupLocation} ➔ ${t.dropLocation}` : t.tripFrequency}
                                            </div>
                                        </td>
                                        <td className="md-td">
                                            <div style={{ fontWeight: 700, color: '#1e293b', fontSize: 12 }}>
                                                {displayDistance} km
                                            </div>
                                        </td>
                                        <td className="md-td">
                                            {(() => {
                                                const tripAllocations = allocations.filter(a => (a.tripId?._id || a.tripId) === t._id);
                                                 const maleCount = tripAllocations.filter(a => a.passengerId?.gender === 'Male').length;
                                                 const count = tripAllocations.length > 0 ? tripAllocations.length : (t.tripType === 'on_demand' ? 1 : 0);
                                                 const capacity = t.vehicleId?.seatingCapacity || 0;
                                                 const isFull = capacity > 0 && count >= capacity;
                                                 return (
                                                     <div 
                                                        style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: count > 0 ? 'pointer' : 'default' }}
                                                        onClick={() => count > 0 && setPassengerListModal({ tripId: t._id, allocations: tripAllocations })}
                                                        title={count > 0 ? "Click to view passenger list" : ""}
                                                     >
                                                         <div style={{
                                                             width: 32, height: 32, borderRadius: 10,
                                                             background: count === 0 ? '#f8fafc' : (isFull ? '#fef2f2' : '#f0f9ff'),
                                                             color: count === 0 ? '#94a3b8' : (isFull ? '#ef4444' : '#0284c7'),
                                                             display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                             fontWeight: 800, fontSize: 13, border: `1px solid ${count === 0 ? '#e2e8f0' : (isFull ? '#fecaca' : '#bae6fd')}`
                                                         }}>
                                                             {count}
                                                         </div>
                                                         <div>
                                                             <div style={{ fontWeight: 700, fontSize: 12, color: count === 0 ? '#94a3b8' : '#1e293b' }}>
                                                                 {count} {count === 1 ? 'Passenger' : 'Passengers'}
                                                             </div>
                                                             <div style={{ fontSize: 10, color: '#94a3b8', display: 'flex', gap: 6 }}>

                                                                  {(maleCount > 0 || femaleCount > 0) && (
                                                                     <>
                                                                         {maleCount > 0 && <span style={{ color: '#0284c7' }}>M: {maleCount}</span>}
                                                                         {femaleCount > 0 && <span style={{ color: '#db2777' }}>F: {femaleCount}</span>}
                                                                     </>
                                                                 )}
                                                             </div>
                                                         </div>
                                                     </div>
                                                 );
                                            })()}
                                        </td>
                                        <td className="md-td">
                                            {(() => {
                                                const inst = getInstanceForTrip(t._id, filters.date);
                                                const vehicle = inst?.vehicleId || t.vehicleId;
                                                const driver = inst?.driverId || t.driverId;

                                                if (t.vendorId && t.vendorStatus === 'Vendor Proposed') {
                                                    return (
                                                        <div style={{ background: '#eff6ff', borderRadius: 10, padding: '10px 12px', border: '1.5px solid #bfdbfe' }}>
                                                            <div style={{ fontSize: 10, fontWeight: 800, color: '#1d4ed8', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'flex', alignItems: 'center', gap: 4 }}><Icon d={icons.truck} size={10} /> Vendor Proposal</div>
                                                            <div style={{ fontSize: 12, fontWeight: 700, color: '#1e40af' }}>{t.vendorId?.vendorName}</div>
                                                            <div style={{ fontSize: 11, color: '#374151', marginTop: 3, display: 'flex', alignItems: 'center', gap: 4 }}><Icon d={icons.truck} size={11} /> {t.vendorProposedVehicleId?.registrationNumber || '—'} {t.vendorProposedVehicleId?.vehicleCode ? `(${t.vendorProposedVehicleId.vehicleCode})` : ''}</div>
                                                            <div style={{ fontSize: 11, color: '#374151', display: 'flex', alignItems: 'center', gap: 4 }}><Icon d={icons.user} size={11} /> {t.vendorProposedDriverId?.name || '—'} {t.vendorProposedDriverId?.driverCode ? `(${t.vendorProposedDriverId.driverCode})` : ''}</div>
                                                            <div style={{ fontSize: 12, fontWeight: 800, color: '#059669', marginTop: 3 }}>{t.vendorProposedAmount ? formatCurrency(t.vendorProposedAmount, operatorDetails?.country) : '—'}</div>
                                                            <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                                                                <button
                                                                    onClick={() => acceptVendorProposal(t)}
                                                                    style={{ flex: 1, padding: '5px 0', background: '#10b981', color: 'white', border: 'none', borderRadius: 6, fontSize: 11, fontWeight: 800, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}
                                                                ><Icon d={icons.check} size={11} color="white" /> Accept</button>
                                                                <button
                                                                    onClick={() => rejectVendorProposal(t)}
                                                                    style={{ flex: 1, padding: '5px 0', background: '#fee2e2', color: '#991b1b', border: 'none', borderRadius: 6, fontSize: 11, fontWeight: 800, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}
                                                                ><Icon d={icons.x} size={11} /> Reject</button>
                                                            </div>
                                                        </div>
                                                    );
                                                }
                                                if (t.vendorId) {
                                                    return (
                                                        <div>
                                                            <div style={{ fontWeight: 600, color: '#16539C' }}>{t.vendorId.vendorName}</div>
                                                            <div style={{ fontSize: 11, color: '#94a3b8' }}>
                                                                {t.vendorStatus === 'Accepted' ? `✓ ${vehicle?.vehicleCode || vehicle?.registrationNumber || t.vendorProposedVehicleId?.vehicleCode || t.vendorProposedVehicleId?.registrationNumber || 'No Vehicle'}` :
                                                                    t.vendorStatus === 'Rejected' ? '✗ Proposal Rejected' :
                                                                        'Awaiting vendor response...'}
                                                            </div>
                                                        </div>
                                                    );
                                                }
                                                if (vehicle) {
                                                    return (
                                                        <div>
                                                            <div style={{ fontWeight: 600 }}>{vehicle.plateNumber || vehicle.registrationNumber || '—'} {vehicle.vehicleCode ? `(${vehicle.vehicleCode})` : ''}</div>
                                                            <div style={{ fontSize: 11, color: '#64748b' }}>{driver?.name || 'No Driver'} {driver?.driverCode ? `(${driver.driverCode})` : ''}</div>
                                                        </div>
                                                    );
                                                }
                                                if (t.requestedVehicleId && (t.status === 'Requested' || t.status === 'PriceProposed')) {
                                                    return (
                                                        <div style={{ background: '#eef2ff', padding: '6px 8px', borderRadius: 8, border: '1px solid #c3dcf5' }}>
                                                            <div style={{ fontSize: 9, fontWeight: 800, color: '#16539C', textTransform: 'uppercase', marginBottom: 2 }}>Client Requested</div>
                                                            <div style={{ fontWeight: 700, fontSize: 11, color: '#1e293b' }}>{t.requestedVehicleId.make} {t.requestedVehicleId.model}</div>
                                                            <div style={{ fontSize: 10, color: '#64748b' }}>{t.requestedVehicleId.seatingCapacity} Seats</div>
                                                        </div>
                                                    );
                                                }
                                                if (!vehicle && !['Requested', 'PriceProposed', 'Cancelled'].includes(t.status)) {
                                                    return (
                                                        <div style={{ padding: '4px 0' }}>
                                                            <div style={{ color: '#ef4444', fontSize: 11, fontWeight: 800, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
                                                                <Icon d={icons.truck} size={12} /> Pending Assignment
                                                            </div>
                                                            <button 
                                                                onClick={() => setModal({ data: t, isDispatchOnly: true })}
                                                                style={{ 
                                                                    display: 'inline-flex', alignItems: 'center', gap: 6, 
                                                                    padding: '7px 14px', borderRadius: 8, border: 'none', 
                                                                    background: 'linear-gradient(135deg, #16539C, #00B2A9)', 
                                                                    color: 'white', fontSize: 11, fontWeight: 800, 
                                                                    cursor: 'pointer', boxShadow: '0 4px 10px rgba(79,70,229,0.25)', 
                                                                    transition: 'transform 0.15s ease' 
                                                                }}
                                                                onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.03)'}
                                                                onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
                                                            >
                                                                Assign Fleet
                                                            </button>
                                                        </div>
                                                    );
                                                }
                                                return <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>Unassigned</span>;
                                            })()}
                                        </td>
                                        <td className="md-td">
                                            <div style={{ fontSize: 12 }}>
                                                {t.tripFrequency === 'One Time' ? (
                                                    <div style={{ fontWeight: 700, color: '#1e293b' }}>
                                                        {t.tripDate ? new Date(t.tripDate).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—'}
                                                    </div>
                                                ) : (
                                                    <div>
                                                        <div style={{ fontSize: 10, color: '#64748b', fontWeight: 700, marginBottom: 5 }}>
                                                            {t.startDate ? new Date(t.startDate).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) : 'Start'} 
                                                            {t.endDate ? ` to ${new Date(t.endDate).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}` : ' onwards'}
                                                        </div>
                                                        <div style={{ display: 'flex', gap: 4 }}>
                                                            {t.recurringDays?.map(d => (
                                                                <span key={d} style={{ background: '#e6f0fa', color: '#11427D', padding: '2px 4px', borderRadius: 4, fontSize: 10, fontWeight: 700 }}>{d.charAt(0)}</span>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                            {t.startTime && (
                                                <div style={{ fontSize: 11, color: '#063A5C', fontWeight: 700, marginTop: 4 }}>
                                                    {t.startTime} {t.endTime ? `- ${t.endTime}` : ''}
                                                </div>
                                            )}
                                        </td>
                                        <td className="md-td">
                                            <div style={{ fontWeight: 700, color: '#10b981', fontSize: 13 }}>
                                                {t.amount ? formatCurrency(t.amount, operatorDetails?.country) : (t.proposedPrice ? formatCurrency(t.proposedPrice, operatorDetails?.country) : '—')}
                                                {(!t.amount && t.proposedPrice && !['Scheduled', 'Completed', 'Cancelled'].includes(t.status)) && (
                                                    <span style={{ fontSize: 9, color: '#94a3b8', marginLeft: 4, fontWeight: 600, textTransform: 'uppercase' }}>(Proposed)</span>
                                                )}
                                            </div>
                                            {t.vendorId && (t.vendorPrice || t.vendorProposedAmount) && (
                                                <div style={{ fontSize: 11, color: '#16539C', marginTop: 4, fontWeight: 700 }}>
                                                    Vendor: {formatCurrency(t.vendorPrice || t.vendorProposedAmount, operatorDetails?.country)}
                                                </div>
                                            )}
                                        </td>
                                        <td className="md-td">
                                            {(() => {
                                                // For recurring trips: always show master status as primary, today's instance as secondary
                                                const todayInst = getInstanceForTrip(t._id, null); // today
                                                const filteredInst = filters.date ? getInstanceForTrip(t._id, filters.date) : null;

                                                const primaryStatus = t.tripFrequency === 'Recurring'
                                                    ? 'Scheduled'   // master recurring trip is always "Scheduled"
                                                    : (t.status || 'Scheduled');

                                                const todayStatus = todayInst?.status;

                                                return (
                                                    <div>
                                                        <span className={`md-badge md-badge-${primaryStatus?.toLowerCase()}`}>{primaryStatus}</span>
                                                        {t.tripFrequency === 'Recurring' && todayStatus && !filters.date && (
                                                            <div style={{ fontSize: 10, color: todayStatus === 'Completed' ? '#10b981' : '#f59e0b', marginTop: 4, fontWeight: 700 }}>
                                                                Today: {todayStatus}
                                                            </div>
                                                        )}
                                                        {t.tripFrequency === 'Recurring' && filteredInst && (
                                                            <div style={{ fontSize: 10, color: filteredInst.status === 'Completed' ? '#10b981' : '#f59e0b', marginTop: 4, fontWeight: 700 }}>
                                                                {filters.date}: {filteredInst.status}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })()}
                                        </td>
                                        <td className="md-td">
                                            <div className="md-actions">
                                                {(() => {
                                                    // For recurring: only use instance status if date filter is set; else use master status
                                                    const inst = filters.date ? getInstanceForTrip(t._id, filters.date) : null;
                                                    const status = t.tripFrequency === 'Recurring'
                                                        ? (inst?.status || t.status)
                                                        : (t.status);

                                                    if (status === 'Scheduled') {
                                                        return (
                                                            <button
                                                                onClick={() => handleUpdateInstanceStatus(t, 'Completed')}
                                                                style={{
                                                                    padding: '6px 12px', borderRadius: 8, border: 'none',
                                                                    background: '#10b981', color: 'white', fontSize: 11,
                                                                    fontWeight: 800, cursor: 'pointer', marginRight: 8
                                                                }}
                                                            >
                                                                Mark as Completed
                                                            </button>
                                                        );
                                                    }
                                                    if (status === 'Completed') {
                                                        return (
                                                            <button
                                                                onClick={() => handleUpdateInstanceStatus(t, 'Scheduled')}
                                                                style={{
                                                                    padding: '6px 12px', borderRadius: 8, border: 'none',
                                                                    background: '#00B2A9', color: 'white', fontSize: 11,
                                                                    fontWeight: 800, cursor: 'pointer', marginRight: 8
                                                                }}
                                                            >
                                                                Mark as Scheduled
                                                            </button>
                                                        );
                                                    }
                                                    return null;
                                                })()}

                                                {t.status === 'Requested' && (
                                                    <button
                                                        onClick={() => setApprovalModal(t)}
                                                        style={{
                                                            padding: '6px 12px', borderRadius: 8, border: 'none',
                                                            background: '#10b981', color: 'white', fontSize: 11,
                                                            fontWeight: 800, cursor: 'pointer', marginRight: 8
                                                        }}
                                                    >
                                                        Review & Propose Price
                                                    </button>
                                                )}
                                                {t.status === 'ClientAccepted' && (
                                                    <button
                                                        onClick={() => setModal({ data: t })}
                                                        style={{
                                                            padding: '6px 12px', borderRadius: 8, border: 'none',
                                                            background: '#00B2A9', color: 'white', fontSize: 11,
                                                            fontWeight: 800, cursor: 'pointer', marginRight: 8
                                                        }}
                                                    >
                                                        Dispatch / Assign Fleet
                                                    </button>
                                                )}
                                                <button className="md-icon-btn" onClick={() => setModal({ data: t })} title="Edit Trip"><Icon d={icons.edit} size={14} /></button>
                                                {t.tripFrequency !== 'Recurring' && (
                                                    <button 
                                                        className="md-icon-btn" 
                                                        onClick={() => setTripSheetModal({ trip: t, instance: null })} 
                                                        title="View Trip Sheet"
                                                        style={{ color: '#10b981' }}
                                                    >
                                                        <Icon d={icons.map} size={14} />
                                                    </button>
                                                )}
                                                <button 
                                                    className="md-icon-btn" 
                                                    onClick={() => onNavigate('trip-history', t._id)} 
                                                    title="View Edit History & Executions"
                                                    style={{ color: '#f59e0b' }}
                                                >
                                                    <Icon d={icons.clock} size={14} />
                                                </button>
                                                <button className="md-icon-btn md-icon-btn-danger" onClick={() => setDeleteModal(t)} title="Delete Trip"><Icon d={icons.trash} size={14} /></button>
                                            </div>
                                        </td>
                                    </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Approval Modal */}
            {approvalModal && (
                <div className="md-modal-overlay" onClick={() => setApprovalModal(null)}>
                    <div className="md-modal" style={{ maxWidth: 500 }} onClick={e => e.stopPropagation()}>
                        <div className="md-modal-header">
                            <h3 className="md-modal-title">Approve Trip Request</h3>
                            <button className="md-modal-close" onClick={() => setApprovalModal(null)}><Icon d={icons.x} size={18} /></button>
                        </div>
                        <div className="md-modal-body">
                            <div style={{ background: '#f8fafc', padding: 12, borderRadius: 12, marginBottom: 16, border: '1px solid #e2e8f0', fontSize: 13 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, borderBottom: '1px solid #e2e8f0', paddingBottom: 6 }}>
                                    <span style={{ fontSize: 11, fontWeight: 800, color: '#64748b', textTransform: 'uppercase' }}>
                                        Client: <strong style={{ color: '#063A5C', textTransform: 'none', fontSize: 13, marginLeft: 4 }}>{approvalModal.clientId?.companyName || '—'}</strong>
                                    </span>
                                    {approvalModal.tripFrequency === 'One Time' && approvalModal.tripDate && (
                                        <span style={{ fontSize: 11, fontWeight: 700, color: '#166534', background: '#f0fdf4', padding: '2px 8px', borderRadius: 6 }}>
                                            Date: {new Date(approvalModal.tripDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                                        </span>
                                    )}
                                    {approvalModal.tripFrequency === 'Recurring' && (approvalModal.startDate || approvalModal.endDate) && (
                                        <span style={{ fontSize: 11, fontWeight: 700, color: '#1d4ed8', background: '#eff6ff', padding: '2px 8px', borderRadius: 6 }}>
                                            Recur: {approvalModal.startDate ? new Date(approvalModal.startDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : '—'}
                                            {'➔'}
                                            {approvalModal.endDate ? new Date(approvalModal.endDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : '—'}
                                        </span>
                                    )}
                                </div>

                                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, color: '#475569', marginBottom: 4 }}>
                                    <div style={{ display: 'flex', gap: 4 }}>
                                        <span style={{ fontWeight: 700, color: '#1e293b', minWidth: 55 }}>Pickup:</span>
                                        <span style={{ color: '#334155', fontSize: 12.5 }}>{approvalModal.pickupLocation}</span>
                                    </div>
                                    <div style={{ display: 'flex', gap: 4 }}>
                                        <span style={{ fontWeight: 700, color: '#1e293b', minWidth: 55 }}>Drop:</span>
                                        <span style={{ color: '#334155', fontSize: 12.5 }}>{approvalModal.dropLocation}</span>
                                    </div>
                                </div>

                                {approvalModal.requestedVehicleId && (
                                    <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <span style={{ fontSize: 11, fontWeight: 800, color: '#16539C', textTransform: 'uppercase' }}>Vehicle:</span>
                                        <span style={{ fontSize: 12, fontWeight: 700, color: '#1e293b' }}>
                                            {approvalModal.requestedVehicleId.make} {approvalModal.requestedVehicleId.model} 
                                            {approvalModal.requestedVehicleId.plateNumber ? ` (${approvalModal.requestedVehicleId.plateNumber})` : ''} 
                                            {approvalModal.requestedVehicleId.seatingCapacity ? ` • ${approvalModal.requestedVehicleId.seatingCapacity} Seats` : ''}
                                        </span>
                                    </div>
                                )}
                            </div>

                            {!approvalModal.routeId && (
                                <div className="md-form-group" style={{ marginBottom: 20 }}>
                                    <label className="md-label">Create New Route As*</label>
                                    <input
                                        className="md-input"
                                        placeholder="Enter name for this new route (e.g. IT Park Shuttle)"
                                        onChange={(e) => approvalModal._routeName = e.target.value}
                                    />
                                    <p style={{ fontSize: 11, color: '#00B2A9', marginTop: 6, fontWeight: 600 }}>* This request proposed a new route. We'll create it automatically.</p>
                                </div>
                            )}

                            <div className="md-form-group">
                                <label className="md-label">Trip Fee / Amount ({operatorDetails?.country ? getCurrencySymbol(operatorDetails.country) : '₹'})*</label>
                                <input
                                    type="number"
                                    className="md-input"
                                    placeholder="Enter trip amount for client billing"
                                    onChange={(e) => approvalModal._amount = e.target.value}
                                    required
                                />
                            </div>
                        </div>
                        <div className="md-modal-footer">
                            <button className="md-cancel-btn" onClick={() => setApprovalModal(null)}>Ignore</button>
                            <button
                                className="md-save-btn"
                                onClick={() => handleApprove(approvalModal, {
                                    routeName: approvalModal._routeName,
                                    amount: approvalModal._amount
                                })}
                            >
                                Confirm & Propose Price
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal */}
            {modal && (
                <TripForm
                    key={modal.data?._id || 'new'}
                    data={modal.data}
                    isDispatchOnlyProp={modal.isDispatchOnly}
                    clients={clients}
                    routes={routes}
                    vehicles={vehicles}
                    drivers={drivers}
                    vendors={vendors}
                    vendorFleet={vendorFleet}
                    trips={trips}
                    mappings={mappings}
                    operatorDetails={operatorDetails}
                    onClose={() => setModal(null)} onNavigate={onNavigate}
                    onSave={handleSave}
                />
            )}

            {/* Trip Sheet Modal */}
            {tripSheetModal && (
                <TripSheetModal 
                    trip={tripSheetModal.trip} 
                    instance={tripSheetModal.instance} 
                    allocations={allocations}
                    onClose={() => setTripSheetModal(null)} 
                    operatorId={operatorId} 
                />
            )}

            {/* Delete Confirmation */}
            {deleteModal && (
                <div className="md-modal-overlay" onClick={() => setDeleteModal(null)}>
                    <div className="md-modal" style={{ maxWidth: 400 }} onClick={e => e.stopPropagation()}>
                        <div className="md-modal-header">
                            <h3 className="md-modal-title" style={{ color: '#ef4444' }}>Confirm Delete</h3>
                        </div>
                        <div className="md-modal-body">
                            Are you sure you want to delete this trip schedule?
                        </div>
                        <div className="md-modal-footer">
                            <button className="md-cancel-btn" onClick={() => setDeleteModal(null)}>Cancel</button>
                            <button className="md-save-btn" style={{ background: '#ef4444' }} onClick={handleDelete}>Delete</button>
                        </div>
                    </div>
                </div>
            )}
            {passengerListModal && (
                <div className="md-modal-overlay" onClick={() => setPassengerListModal(null)}>
                    <div className="md-modal" style={{ maxWidth: 600 }} onClick={e => e.stopPropagation()}>
                        <div className="md-modal-header" style={{ background: '#063A5C' }}>
                            <h3 className="md-modal-title" style={{ color: 'white' }}>Manifest: Passenger List</h3>
                            <button className="md-modal-close" style={{ color: 'white' }} onClick={() => setPassengerListModal(null)}><Icon d={icons.x} size={18} /></button>
                        </div>
                        <div className="md-modal-body" style={{ padding: 0 }}>
                            {passengerListModal.allocations.length === 0 ? (
                                <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>No passenger data available for this trip.</div>
                            ) : (
                                <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                        <thead style={{ position: 'sticky', top: 0, background: '#f8fafc', zIndex: 1 }}>
                                            <tr>
                                                <th style={{ textAlign: 'left', padding: '12px 20px', fontSize: 11, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', borderBottom: '1px solid #e2e8f0' }}>Passenger</th>
                                                <th style={{ textAlign: 'left', padding: '12px 20px', fontSize: 11, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', borderBottom: '1px solid #e2e8f0' }}>Contact Details</th>
                                                <th style={{ textAlign: 'center', padding: '12px 20px', fontSize: 11, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', borderBottom: '1px solid #e2e8f0' }}>Gender</th>
                                                <th style={{ textAlign: 'center', padding: '12px 20px', fontSize: 11, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', borderBottom: '1px solid #e2e8f0' }}>Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {passengerListModal.allocations.map((a, idx) => {
                                                const p = a.passengerId;
                                                if (!p) return null;
                                                const statusColor = a.status === 'Boarded' ? '#16a34a' : a.status === 'Absent' ? '#ef4444' : a.status === 'No Show' ? '#f59e0b' : '#64748b';
                                                const statusBg = a.status === 'Boarded' ? '#dcfce7' : a.status === 'Absent' ? '#fee2e2' : a.status === 'No Show' ? '#fef3c7' : '#f1f5f9';
                                                return (
                                                <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                                    <td style={{ padding: '12px 20px' }}>
                                                        <div style={{ fontWeight: 700, color: '#063A5C', fontSize: 14 }}>{p.name}</div>
                                                        <div style={{ fontSize: 11, color: '#94a3b8' }}>ID: {p.employeeId || 'N/A'}</div>
                                                    </td>
                                                    <td style={{ padding: '12px 20px' }}>
                                                        <div style={{ fontSize: 13, fontWeight: 600, color: '#475569' }}>{p.contactNumber}</div>
                                                        <div style={{ fontSize: 11, color: '#64748b' }}>{p.email || 'No email provided'}</div>
                                                    </td>
                                                    <td style={{ padding: '12px 20px', textAlign: 'center' }}>
                                                        <span style={{
                                                            display: 'inline-block', padding: '4px 10px', borderRadius: 20, fontSize: 10, fontWeight: 800,
                                                            background: p.gender === 'Female' ? '#fdf2f8' : (p.gender === 'Male' ? '#eff6ff' : '#f1f5f9'),
                                                            color: p.gender === 'Female' ? '#db2777' : (p.gender === 'Male' ? '#2563eb' : '#64748b'),
                                                            border: `1px solid ${p.gender === 'Female' ? '#fbcfe8' : (p.gender === 'Male' ? '#bfdbfe' : '#e2e8f0')}`
                                                        }}>
                                                            {p.gender || 'Not Specified'}
                                                        </span>
                                                    </td>
                                                    <td style={{ padding: '12px 20px', textAlign: 'center' }}>
                                                        <span style={{
                                                            display: 'inline-block', padding: '4px 10px', borderRadius: 20, fontSize: 10, fontWeight: 800,
                                                            background: statusBg, color: statusColor
                                                        }}>
                                                            {a.status || 'Pending'}
                                                        </span>
                                                    </td>
                                                </tr>
                                            )})}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                        <div className="md-modal-footer">
                            <div style={{ fontSize: 12, color: '#94a3b8', fontWeight: 600 }}>Total: {passengerListModal.allocations.length} passengers</div>
                            <button className="md-cancel-btn" onClick={() => setPassengerListModal(null)}>Close Manifest</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function TripForm({ onNavigate, data, isDispatchOnlyProp, clients, routes, vehicles, drivers, vendors, vendorFleet, trips, mappings = [], operatorDetails, onClose, onSave }) {
    const [form, setForm] = useState(data ? {
        ...data,
        tripName: data.tripName || '',
        clientId: data.clientId?._id || data.clientId || '',
        routeId: data.routeId?._id || data.routeId || '',
        vehicleId: data.vehicleId?._id || data.vehicleId || '',
        driverId: data.driverId?._id || data.driverId || '',
        vendorId: data.vendorId?._id || data.vendorId || '',
        amount: data.amount || '',
        billingType: data.billingType || (data.tripType === 'on_demand' ? 'Ad-hoc' : 'Daily'),
        startDate: data.startDate || '',
        endDate: data.endDate || '',
        changeReason: ''
    } : {
        tripName: '',
        clientId: '', routeId: '',
        amount: '',
        billingType: 'Daily',
        pickupLocation: '', dropLocation: '',
        tripType: 'Login', tripFrequency: 'One Time',
        tripDate: '', recurringDays: [], status: 'Scheduled',
        startTime: '', endTime: '',
        vehicleId: '', driverId: '', vendorId: '',
        startDate: '', endDate: '',
        changeReason: ''
    });

    const [routeStops, setRouteStops] = useState([]);
    const [isVendorTrip, setIsVendorTrip] = useState(!!form.vendorId);
    const [availPanel, setAvailPanel] = useState(null); // 'fleet' | 'drivers' | 'vfleet'

    const formatDateToDMY = (dateStr) => {
        if (!dateStr) return '';
        if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) return dateStr;
        if (dateStr.includes('/')) return dateStr;
        const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (match) {
            return `${match[3]}/${match[2]}/${match[1]}`;
        }
        return dateStr;
    };

    const parseDMYToYYYYMMDD = (dmyStr) => {
        if (!dmyStr) return '';
        const parts = dmyStr.split('/');
        if (parts.length === 3) {
            const dd = parseInt(parts[0], 10);
            const mm = parseInt(parts[1], 10);
            const yyyy = parseInt(parts[2], 10);
            if (isNaN(dd) || isNaN(mm) || isNaN(yyyy)) return '';
            if (mm < 1 || mm > 12) return '';
            if (dd < 1 || dd > 31) return '';
            
            const dateObj = new Date(yyyy, mm - 1, dd);
            if (dateObj.getFullYear() === yyyy && dateObj.getMonth() === mm - 1 && dateObj.getDate() === dd) {
                const ddStr = String(dd).padStart(2, '0');
                const mmStr = String(mm).padStart(2, '0');
                return `${yyyy}-${mmStr}-${ddStr}`;
            }
        }
        return '';
    };

    const formatDMYInput = (value) => {
        const clean = value.replace(/\D/g, '').slice(0, 8);
        let formatted = clean;
        if (clean.length > 4) {
            formatted = `${clean.slice(0, 2)}/${clean.slice(2, 4)}/${clean.slice(4)}`;
        } else if (clean.length > 2) {
            formatted = `${clean.slice(0, 2)}/${clean.slice(2)}`;
        }
        return formatted;
    };

    const handleDateChange = (field, value, nativeEvent) => {
        const isDeleting = nativeEvent && nativeEvent.inputType === 'deleteContentBackward';
        if (isDeleting) {
            setForm(prev => ({ ...prev, [field]: value }));
            return;
        }
        const dmy = formatDMYInput(value);
        const ymd = parseDMYToYYYYMMDD(dmy);
        setForm(prev => ({ ...prev, [field]: ymd || dmy }));
    };

    const handleConfirm = () => {
        const formToSave = { ...form };
        if (form.tripFrequency === 'One Time') {
            if (!form.tripDate) {
                alert('Trip Date is required.');
                return;
            }
            formToSave.tripDate = String(form.tripDate).split('T')[0];
            if (!/^\d{4}-\d{2}-\d{2}$/.test(formToSave.tripDate)) {
                alert('Please enter a valid Trip Date in DD/MM/YYYY format.');
                return;
            }
        } else {
            if (!form.startDate) {
                alert('Effective From date is required.');
                return;
            }
            formToSave.startDate = String(form.startDate).split('T')[0];
            if (!/^\d{4}-\d{2}-\d{2}$/.test(formToSave.startDate)) {
                alert('Please enter a valid Effective From date in DD/MM/YYYY format.');
                return;
            }
            if (!form.endDate) {
                const sDate = new Date(formToSave.startDate);
                const eDate = new Date(sDate.getFullYear(), sDate.getMonth() + 1, 0);
                formToSave.endDate = eDate.getFullYear() + '-' + String(eDate.getMonth() + 1).padStart(2, '0') + '-' + String(eDate.getDate()).padStart(2, '0');
            } else {
                formToSave.endDate = String(form.endDate).split('T')[0];
                if (!/^\d{4}-\d{2}-\d{2}$/.test(formToSave.endDate)) {
                    alert('Please enter a valid Effective To date in DD/MM/YYYY format.');
                    return;
                }
            }
        }

        if (!form.startTime) {
            alert('Start Time is required.');
            return;
        }
        if (!form.endTime) {
            alert('End Time is required.');
            return;
        }

        onSave(formToSave);
    };

    const mapRef = useRef(null);
    const tripDateRef = useRef(null);
    const startDateRef = useRef(null);
    const endDateRef = useRef(null);
    const mapInstance = useRef(null);
    const routeLayer = useRef(null);
    const pickupMarker = useRef(null);
    const dropMarker = useRef(null);

    const [pickupQuery, setPickupQuery] = useState('');
    const [dropQuery, setDropQuery] = useState('');
    const [pickupSuggestions, setPickupSuggestions] = useState([]);
    const [dropSuggestions, setDropSuggestions] = useState([]);
    const [showPickupSuggestions, setShowPickupSuggestions] = useState(false);
    const [showDropSuggestions, setShowDropSuggestions] = useState(false);

    // Initialize Map
    useEffect(() => {
        if (!mapInstance.current && mapRef.current) {
            mapInstance.current = L.map(mapRef.current).setView([20.5937, 78.9629], 5);
            L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png', {
                attribution: '&copy; OpenStreetMap contributors'
            }).addTo(mapInstance.current);

            delete L.Icon.Default.prototype._getIconUrl;
            L.Icon.Default.mergeOptions({
                iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
                iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
                shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
            });
        }
    }, []);

    const searchLocation = async (query) => {
        if (!query || query.length < 3) return [];
        try {
            const res = await axios.get(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=5`);
            return res.data;
        } catch (error) { return []; }
    };

    useEffect(() => {
        const delayDebounceFn = setTimeout(async () => {
            if (pickupQuery && pickupQuery !== form.pickupLocation) {
                const results = await searchLocation(pickupQuery);
                setPickupSuggestions(results);
                setShowPickupSuggestions(true);
            } else {
                setPickupSuggestions([]);
            }
        }, 500);
        return () => clearTimeout(delayDebounceFn);
    }, [pickupQuery, form.pickupLocation]);

    useEffect(() => {
        const delayDebounceFn = setTimeout(async () => {
            if (dropQuery && dropQuery !== form.dropLocation) {
                const results = await searchLocation(dropQuery);
                setDropSuggestions(results);
                setShowDropSuggestions(true);
            } else {
                setDropSuggestions([]);
            }
        }, 500);
        return () => clearTimeout(delayDebounceFn);
    }, [dropQuery, form.dropLocation]);

    const handleSelectLocation = (place, type) => {
        const lat = parseFloat(place.lat);
        const lon = parseFloat(place.lon);
        const displayName = place.display_name;

        if (type === 'pickup') {
            setForm(prev => ({ ...prev, pickupLocation: displayName, pickupLat: lat, pickupLng: lon }));
            setPickupQuery(displayName);
            setShowPickupSuggestions(false);
            if (pickupMarker.current) mapInstance.current.removeLayer(pickupMarker.current);
            pickupMarker.current = L.marker([lat, lon], { title: 'Pickup' }).addTo(mapInstance.current);
        } else {
            setForm(prev => ({ ...prev, dropLocation: displayName, dropLat: lat, dropLng: lon }));
            setDropQuery(displayName);
            setShowDropSuggestions(false);
            if (dropMarker.current) mapInstance.current.removeLayer(dropMarker.current);
            dropMarker.current = L.marker([lat, lon], { title: 'Drop' }).addTo(mapInstance.current);
        }

        if (mapInstance.current) {
            mapInstance.current.setView([lat, lon], 12);
        }
    };

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (!e.target.closest('.pickup-search-container')) setShowPickupSuggestions(false);
            if (!e.target.closest('.drop-search-container')) setShowDropSuggestions(false);
        };
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    useEffect(() => {
        if (data && data.pickupLocation) setPickupQuery(data.pickupLocation);
        if (data && data.dropLocation) setDropQuery(data.dropLocation);
    }, [data]);

    useEffect(() => {
        const calculateRoute = async () => {
            if (!mapInstance.current || !form.pickupLat || !form.pickupLng || !form.dropLat || !form.dropLng) return;
            try {
                const url = `https://router.project-osrm.org/route/v1/driving/${form.pickupLng},${form.pickupLat};${form.dropLng},${form.dropLat}?overview=full&geometries=geojson`;
                const response = await axios.get(url);
                
                if (response.data && response.data.routes && response.data.routes.length > 0) {
                    const route = response.data.routes[0];
                    if (routeLayer.current) mapInstance.current.removeLayer(routeLayer.current);
                    const geojsonRoute = { type: "Feature", properties: {}, geometry: route.geometry };
                    routeLayer.current = L.geoJSON(geojsonRoute, {
                        style: { color: '#16539C', weight: 5, opacity: 0.7 }
                    }).addTo(mapInstance.current);
                    mapInstance.current.fitBounds(routeLayer.current.getBounds(), { padding: [50, 50] });
                }
            } catch (error) { console.error("OSRM error:", error); }
        };
        const timer = setTimeout(calculateRoute, 800); 
        return () => clearTimeout(timer);
    }, [form.pickupLat, form.pickupLng, form.dropLat, form.dropLng]);

    const fetchRouteStops = async (routeId) => {
        try {
            const res = await axios.get(`${API}/route-stops/${routeId}`);
            setRouteStops(res.data);
            showStopsOnMap(res.data);
        } catch (err) { console.error(err); }
    };

    const showStopsOnMap = (stops) => {
        if (!mapInstance.current) return;
        if (stops.length > 0) {
            mapInstance.current.setView([stops[0].lat, stops[0].lng], 13);
        }
        stops.forEach(stop => {
            L.circleMarker([stop.lat, stop.lng], {
                radius: 6,
                fillColor: "#fff",
                fillOpacity: 1,
                color: "#00B2A9",
                weight: 2,
            }).bindTooltip(stop.stopName || stop.name).addTo(mapInstance.current);
        });
    };

    useEffect(() => {
        if (form.routeId) fetchRouteStops(form.routeId);
    }, []);

    const toggleDay = (day) => {
        const days = form.recurringDays.includes(day)
            ? form.recurringDays.filter(d => d !== day)
            : [...form.recurringDays, day];
        setForm({ ...form, recurringDays: days });
    };

    const isDispatchOnly = isDispatchOnlyProp || form.status === 'ClientAccepted';

    return (
        <>
            {availPanel && (
                <AvailabilityPanel
                    onClose={() => setAvailPanel(null)}
                    vehicles={vehicles}
                    drivers={drivers}
                    vendorFleet={vendorFleet}
                    trips={trips || []}
                />
            )}
            <div className="md-modal-overlay" onClick={onClose}>
                <div className="md-modal" style={{ maxWidth: isDispatchOnly ? 550 : 1100, width: '95%' }} onClick={e => e.stopPropagation()}>
                    <div className="md-modal-header">
                        <h3 className="md-modal-title">{isDispatchOnly ? 'Dispatch / Assign Fleet' : (data ? 'Edit Trip Schedule' : 'Create New Trip')}</h3>
                        <button className="md-modal-close" onClick={onClose}><Icon d={icons.x} size={18} /></button>
                    </div>
                    <div className="md-modal-body" style={{ display: isDispatchOnly ? 'block' : 'grid', gridTemplateColumns: isDispatchOnly ? 'none' : '1fr 1.25fr', gap: '30px', padding: 25 }}>
                        <div className="form-fields" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                            {!isDispatchOnly && (
                                <>
                                    <div className="md-form-group" style={{ gridColumn: 'span 2' }}>
                                        <label className="md-label">Trip Name (Optional)</label>
                                        <input 
                                            className="md-input" 
                                            placeholder="e.g. Morning Shift Shuttle" 
                                            value={form.tripName} 
                                            onChange={e => setForm({ ...form, tripName: e.target.value })} 
                                        />
                                    </div>
                                    {data?.requestedVehicleId && (
                                        <div style={{ gridColumn: 'span 2', background: '#eef2ff', padding: 12, borderRadius: 12, border: '1px solid #c3dcf5', display: 'flex', alignItems: 'center', gap: 12 }}>
                                            <div style={{ width: 36, height: 36, borderRadius: 8, background: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                <Icon d={icons.truck} size={18} color="#16539C" />
                                            </div>
                                            <div>
                                                <div style={{ fontSize: 11, fontWeight: 800, color: '#16539C', textTransform: 'uppercase' }}>Client Requested Vehicle</div>
                                                <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b' }}>
                                                    {data.requestedVehicleId.make} {data.requestedVehicleId.model} ({data.requestedVehicleId.seatingCapacity} Seats)
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    <div className="md-form-group">
                                        <label className="md-label">Client*</label>
                                        <select className="md-input" value={form.clientId?._id || form.clientId} onChange={e => setForm({ ...form, clientId: e.target.value })}>
                                            <option value="">Select Client</option>
                                            {clients.map(c => <option key={c._id} value={c._id}>{c.companyName}</option>)}
                                        </select>
                                    </div>
                                    <div className="md-form-group">
                                        <label className="md-label">Trip Type*</label>
                                        <select 
                                            className="md-input" 
                                            value={form.tripType} 
                                            onChange={e => {
                                                const newType = e.target.value;
                                                const r = routes.find(x => x._id === form.routeId);
                                                if (r) {
                                                    const isLogout = newType === 'Logout';
                                                    setForm({
                                                        ...form,
                                                        tripType: newType,
                                                        pickupLocation: isLogout ? r.dropLocation : r.pickupLocation,
                                                        dropLocation: isLogout ? r.pickupLocation : r.dropLocation,
                                                        pickupLat: isLogout ? r.dropLat : r.pickupLat,
                                                        pickupLng: isLogout ? r.dropLng : r.pickupLng,
                                                        dropLat: isLogout ? r.pickupLat : r.dropLat,
                                                        dropLng: isLogout ? r.pickupLng : r.dropLng,
                                                    });
                                                } else {
                                                    setForm({ ...form, tripType: newType });
                                                }
                                            }}
                                        >
                                            <option value="Login">Login (Pickup)</option>
                                            <option value="Logout">Logout (Drop)</option>
                                            {form.tripType === 'on_demand' && <option value="on_demand">Adhoc Trip</option>}
                                        </select>
                                    </div>
                                    <div className="md-form-group" style={{ gridColumn: 'span 2' }}>
                                        <label className="md-label">Billing Category*</label>
                                        <select className="md-input" value={form.billingType} onChange={e => setForm({ ...form, billingType: e.target.value })} required>
                                            <option value="Daily">Daily</option>
                                            <option value="Monthly">Monthly</option>
                                            <option value="Shuttle">Shuttle</option>
                                            <option value="Shuttle with Booking">Shuttle with Booking</option>
                                            <option value="Ad-hoc">Ad-hoc</option>
                                        </select>
                                    </div>
                                    <div className="md-form-group" style={{ gridColumn: 'span 2' }}>
                                        <label className="md-label">Route Selection*</label>
                                        <select
                                            className="md-input"
                                            value={form.routeId}
                                            onChange={e => {
                                                const rid = e.target.value;
                                                const r = routes.find(x => x._id === rid);
                                                const isLogout = form.tripType === 'Logout';
                                                setForm({
                                                    ...form,
                                                    routeId: rid,
                                                    pickupLocation: isLogout ? (r?.dropLocation || '') : (r?.pickupLocation || ''),
                                                    dropLocation: isLogout ? (r?.pickupLocation || '') : (r?.dropLocation || ''),
                                                    pickupLat: isLogout ? r?.dropLat : r?.pickupLat,
                                                    pickupLng: isLogout ? r?.dropLng : r?.pickupLng,
                                                    dropLat: isLogout ? r?.pickupLat : r?.dropLat,
                                                    dropLng: isLogout ? r?.pickupLng : r?.dropLng,
                                                });
                                                if (rid) fetchRouteStops(rid);
                                            }}
                                        >
                                            <option value="">Link to an existing route...</option>
                                            {routes
                                                .filter(r => !form.clientId || (r.clientId?._id || r.clientId) === (form.clientId?._id || form.clientId))
                                                .map(r => <option key={r._id} value={r._id}>{r.name} ({r.distance}km)</option>)}
                                        </select>
                                    </div>
                                </>
                            )}

                            {isDispatchOnly && (
                                <div style={{ gridColumn: 'span 2', padding: '15px', background: '#eff6ff', borderRadius: 12, border: '1px solid #bfdbfe', marginBottom: 5 }}>
                                    <div style={{ fontSize: 13, fontWeight: 800, color: '#1e40af', marginBottom: 8 }}>Trip Details Summary</div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 20px' }}>
                                        <div>
                                            <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>Client</div>
                                            <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b' }}>{clients.find(c => (c._id === (form.clientId?._id || form.clientId)))?.companyName || '—'}</div>
                                        </div>
                                        <div>
                                            <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>Trip Type</div>
                                            <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b' }}>{form.tripType} · {form.billingType}</div>
                                        </div>
                                        <div style={{ gridColumn: 'span 2' }}>
                                            <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>Route</div>
                                            <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b' }}>{form.pickupLocation} ➔ {form.dropLocation}</div>
                                        </div>
                                        <div>
                                            <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>Date & Time</div>
                                            <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b' }}>
                                                {form.tripDate ? new Date(form.tripDate).toLocaleDateString() : '—'} @ {form.startTime}
                                            </div>
                                        </div>
                                        <div>
                                            <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>Final Amount</div>
                                            <div style={{ fontSize: 14, fontWeight: 900, color: '#059669' }}>{formatCurrency(form.amount, operatorDetails?.country)}</div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="md-form-group" style={{ gridColumn: 'span 2' }}>
                                <div style={{ display: 'flex', gap: 15, alignItems: 'center', marginBottom: 8, padding: '10px 12px', background: '#f8fafc', borderRadius: 10, border: '1px solid #e2e8f0' }}>
                                    <label className="md-label" style={{ margin: 0, fontSize: 13, userSelect: 'none', cursor: 'pointer' }}>
                                        <input
                                            type="checkbox"
                                            style={{ marginRight: 8 }}
                                            checked={isVendorTrip}
                                            onChange={e => {
                                                setIsVendorTrip(e.target.checked);
                                                if (!e.target.checked) setForm(prev => ({ ...prev, vendorId: '', vehicleId: '', driverId: '' }));
                                            }}
                                        />
                                        Assign to Vendor?
                                    </label>
                                </div>

                                {isVendorTrip ? (
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginTop: 10 }}>
                                        <div className="md-form-group">
                                            <label className="md-label">Vendor</label>
                                            <select className="md-input" value={form.vendorId} onChange={e => setForm({ ...form, vendorId: e.target.value, vehicleId: '', driverId: '' })}>
                                                <option value="">Select Vendor</option>
                                                {vendors.map(v => <option key={v._id} value={v._id}>{v.vendorName}</option>)}
                                            </select>
                                        </div>
                                        <div className="md-form-group">
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                                                <label className="md-label" style={{ margin: 0 }}>Vendor Vehicle</label>
                                                <button type="button" onClick={() => setAvailPanel('vfleet')} title="Check vendor fleet availability" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 7, border: '1px solid #c3dcf5', background: '#eef2ff', color: '#16539C', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                                                    <Icon d={icons.calendar} size={11} strokeWidth={2} /> Availability
                                                </button>
                                            </div>
                                            <select className="md-input" style={{ borderColor: (() => {
                                                if (!form.vehicleId || !form.startTime) return '#e2e8f0';
                                                const sh = parseInt(form.startTime.split(':')[0]);
                                                const eh = parseInt((form.endTime || form.startTime).split(':')[0]);
                                                const targetDate = form.tripDate ? String(form.tripDate).split('T')[0] : null;
                                                const conflict = trips.some(t => {
                                                    const isSameEntity = (t.vehicleId?._id === form.vehicleId || t.vehicleId === form.vehicleId || t.vendorProposedVehicleId?._id === form.vehicleId || t.vendorProposedVehicleId === form.vehicleId);
                                                    if (!isSameEntity || !['Scheduled', 'Ongoing'].includes(t.status) || t._id === form._id) return false;

                                                    // Date/Recurring check
                                                    const isConflictDate = (
                                                        (t.tripFrequency === 'One Time' && targetDate && t.tripDate && t.tripDate.startsWith(targetDate)) ||
                                                        (t.tripFrequency === 'Recurring' && form.tripFrequency === 'Recurring' && t.recurringDays?.some(d => form.recurringDays?.includes(d))) ||
                                                        (t.tripFrequency === 'Recurring' && form.tripFrequency === 'One Time' && targetDate && t.recurringDays?.includes(new Date(targetDate).toLocaleDateString('en-US', { weekday: 'short' }))) ||
                                                        (t.tripFrequency === 'One Time' && form.tripFrequency === 'Recurring' && t.tripDate && form.recurringDays?.includes(new Date(t.tripDate).toLocaleDateString('en-US', { weekday: 'short' })))
                                                    );

                                                    if (!isConflictDate) return false;

                                                    return (t.billingType === 'Monthly' || (t.startTime && (() => {
                                                        const tsh = parseInt(t.startTime.split(':')[0]);
                                                        const teh = parseInt((t.endTime || t.startTime).split(':')[0]);
                                                        return (sh <= teh && eh >= tsh);
                                                    })()));
                                                });
                                                return conflict ? '#ef4444' : '#e2e8f0';
                                            })() }} value={form.vehicleId} onChange={e => setForm({ ...form, vehicleId: e.target.value })}>
                                                <option value="">Select Vehicle</option>
                                                {vendorFleet.filter(f => f.vendorId?._id === form.vendorId || f.vendorId === form.vendorId).map(f => (
                                                    <option key={f._id} value={f._id}>{f.registrationNumber || f.plateNumber} ({f.vehicleType}) {f.vehicleCode ? `(${f.vehicleCode})` : ''}</option>
                                                ))}
                                            </select>
                                            {form.vehicleId && (
                                                <div style={{ marginTop: 8 }}>
                                                    <div style={{ display: 'flex', gap: 2, height: 6, borderRadius: 3, overflow: 'hidden' }}>
                                                        {Array.from({ length: 24 }).map((_, h) => {
                                                            const sh = form.startTime ? parseInt(form.startTime.split(':')[0]) : -1;
                                                            const eh = (form.endTime || form.startTime) ? parseInt((form.endTime || form.startTime).split(':')[0]) : -1;
                                                            const inWindow = sh !== -1 && h >= sh && h <= eh;
                                                            const targetDate = form.tripDate ? String(form.tripDate).split('T')[0] : null;
                                                            const busy = trips.some(t => {
                                                                const isSameEntity = (t.vehicleId?._id === form.vehicleId || t.vehicleId === form.vehicleId || t.vendorProposedVehicleId?._id === form.vehicleId || t.vendorProposedVehicleId === form.vehicleId);
                                                                if (!isSameEntity || !['Scheduled', 'Ongoing'].includes(t.status) || t._id === form._id) return false;

                                                                const isConflictDate = (
                                                                    (t.tripFrequency === 'One Time' && targetDate && t.tripDate && t.tripDate.startsWith(targetDate)) ||
                                                                    (t.tripFrequency === 'Recurring' && form.tripFrequency === 'Recurring' && t.recurringDays?.some(d => form.recurringDays?.includes(d))) ||
                                                                    (t.tripFrequency === 'Recurring' && form.tripFrequency === 'One Time' && targetDate && t.recurringDays?.includes(new Date(targetDate).toLocaleDateString('en-US', { weekday: 'short' }))) ||
                                                                    (t.tripFrequency === 'One Time' && form.tripFrequency === 'Recurring' && t.tripDate && form.recurringDays?.includes(new Date(t.tripDate).toLocaleDateString('en-US', { weekday: 'short' })))
                                                                );

                                                                if (!isConflictDate) return false;

                                                                return (t.billingType === 'Monthly' || (t.startTime && h >= parseInt(t.startTime.split(':')[0]) && h <= parseInt((t.endTime || t.startTime).split(':')[0])));
                                                            });
                                                            return <div key={h} title={busy ? `Busy at ${h}:00` : `Available at ${h}:00`} style={{ flex: 1, background: busy ? '#ef4444' : (inWindow ? '#16539C' : '#e2e8f0'), borderTop: inWindow ? '2px solid #fff' : 'none' }} />;
                                                        })}
                                                    </div>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 3, fontSize: 8, color: '#94a3b8', fontWeight: 600 }}>
                                                        <span>12AM</span><span>6AM</span><span>12PM</span><span>6PM</span><span>11PM</span>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginTop: 10 }}>
                                        <div className="md-form-group">
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                                                <label className="md-label" style={{ margin: 0 }}>Vehicle</label>
                                                <button type="button" onClick={() => setAvailPanel('fleet')} title="Check fleet availability" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 7, border: '1px solid #c3dcf5', background: '#eef2ff', color: '#16539C', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                                                    <Icon d={icons.calendar} size={11} strokeWidth={2} /> Availability
                                                </button>
                                            </div>
                                            <select className="md-input" style={{ borderColor: (() => {
                                                if (!form.vehicleId || !form.startTime) return '#e2e8f0';
                                                const sh = parseInt(form.startTime.split(':')[0]);
                                                const eh = parseInt((form.endTime || form.startTime).split(':')[0]);
                                                const targetDate = form.tripDate ? String(form.tripDate).split('T')[0] : null;
                                                const conflict = trips.some(t => {
                                                    const isSameEntity = (t.vehicleId?._id === form.vehicleId || t.vehicleId === form.vehicleId);
                                                    if (!isSameEntity || !['Scheduled', 'Ongoing'].includes(t.status) || t._id === form._id) return false;

                                                    const isConflictDate = (
                                                        (t.tripFrequency === 'One Time' && targetDate && t.tripDate && t.tripDate.startsWith(targetDate)) ||
                                                        (t.tripFrequency === 'Recurring' && form.tripFrequency === 'Recurring' && t.recurringDays?.some(d => form.recurringDays?.includes(d))) ||
                                                        (t.tripFrequency === 'Recurring' && form.tripFrequency === 'One Time' && targetDate && t.recurringDays?.includes(new Date(targetDate).toLocaleDateString('en-US', { weekday: 'short' }))) ||
                                                        (t.tripFrequency === 'One Time' && form.tripFrequency === 'Recurring' && t.tripDate && form.recurringDays?.includes(new Date(t.tripDate).toLocaleDateString('en-US', { weekday: 'short' })))
                                                    );

                                                    if (!isConflictDate) return false;

                                                    return (t.billingType === 'Monthly' || (t.startTime && (() => {
                                                        const tsh = parseInt(t.startTime.split(':')[0]);
                                                        const teh = parseInt((t.endTime || t.startTime).split(':')[0]);
                                                        return (sh <= teh && eh >= tsh);
                                                    })()));
                                                });
                                                return conflict ? '#ef4444' : '#e2e8f0';
                                            })() }} value={form.vehicleId} onChange={e => {
                                                const val = e.target.value;
                                                let nextDriverId = form.driverId;
                                                if (val && Array.isArray(mappings)) {
                                                    const m = mappings.find(x => (x.vehicleId?._id || x.vehicleId) === val);
                                                    if (m) {
                                                        nextDriverId = m.driverId?._id || m.driverId || form.driverId;
                                                    }
                                                }
                                                setForm(prev => ({ ...prev, vehicleId: val, driverId: nextDriverId }));
                                            }}>
                                                <option value="">Select Vehicle</option>
                                                {vehicles.map(v => <option key={v._id} value={v._id}>{v.registrationNumber || v.plateNumber} - {v.make} {v.vehicleCode ? `(${v.vehicleCode})` : ''}</option>)}
                                            </select>
                                            {form.vehicleId && (
                                                <div style={{ marginTop: 8 }}>
                                                    <div style={{ display: 'flex', gap: 2, height: 6, borderRadius: 3, overflow: 'hidden' }}>
                                                        {Array.from({ length: 24 }).map((_, h) => {
                                                            const sh = form.startTime ? parseInt(form.startTime.split(':')[0]) : -1;
                                                            const eh = (form.endTime || form.startTime) ? parseInt((form.endTime || form.startTime).split(':')[0]) : -1;
                                                            const inWindow = sh !== -1 && h >= sh && h <= eh;
                                                            const targetDate = form.tripDate ? String(form.tripDate).split('T')[0] : null;
                                                            const busy = trips.some(t => {
                                                                const isSameEntity = (t.vehicleId?._id === form.vehicleId || t.vehicleId === form.vehicleId);
                                                                if (!isSameEntity || !['Scheduled', 'Ongoing'].includes(t.status) || t._id === form._id) return false;

                                                                const isConflictDate = (
                                                                    (t.tripFrequency === 'One Time' && targetDate && t.tripDate && t.tripDate.startsWith(targetDate)) ||
                                                                    (t.tripFrequency === 'Recurring' && form.tripFrequency === 'Recurring' && t.recurringDays?.some(d => form.recurringDays?.includes(d))) ||
                                                                    (t.tripFrequency === 'Recurring' && form.tripFrequency === 'One Time' && targetDate && t.recurringDays?.includes(new Date(targetDate).toLocaleDateString('en-US', { weekday: 'short' }))) ||
                                                                    (t.tripFrequency === 'One Time' && form.tripFrequency === 'Recurring' && t.tripDate && form.recurringDays?.includes(new Date(t.tripDate).toLocaleDateString('en-US', { weekday: 'short' })))
                                                                );

                                                                if (!isConflictDate) return false;

                                                                return (t.billingType === 'Monthly' || (t.startTime && h >= parseInt(t.startTime.split(':')[0]) && h <= parseInt((t.endTime || t.startTime).split(':')[0])));
                                                            });
                                                            return <div key={h} title={busy ? `Busy at ${h}:00` : `Available at ${h}:00`} style={{ flex: 1, background: busy ? '#ef4444' : (inWindow ? '#16539C' : '#e2e8f0'), borderTop: inWindow ? '2px solid #fff' : 'none' }} />;
                                                        })}
                                                    </div>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 3, fontSize: 8, color: '#94a3b8', fontWeight: 600 }}>
                                                        <span>12AM</span><span>6AM</span><span>12PM</span><span>6PM</span><span>11PM</span>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                        <div className="md-form-group">
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                                                <label className="md-label" style={{ margin: 0 }}>Driver</label>
                                                <button type="button" onClick={() => setAvailPanel('drivers')} title="Check driver availability" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 7, border: '1px solid #c3dcf5', background: '#eef2ff', color: '#16539C', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                                                    <Icon d={icons.calendar} size={11} strokeWidth={2} /> Availability
                                                </button>
                                            </div>
                                            <select className="md-input" style={{ borderColor: (() => {
                                                if (!form.driverId || !form.startTime) return '#e2e8f0';
                                                const sh = parseInt(form.startTime.split(':')[0]);
                                                const eh = parseInt((form.endTime || form.startTime).split(':')[0]);
                                                const targetDate = form.tripDate ? String(form.tripDate).split('T')[0] : null;
                                                const conflict = trips.some(t => {
                                                    const isSameEntity = (t.driverId?._id === form.driverId || t.driverId === form.driverId || t.vendorProposedDriverId?._id === form.driverId || t.vendorProposedDriverId === form.driverId);
                                                    if (!isSameEntity || !['Scheduled', 'Ongoing'].includes(t.status) || t._id === form._id) return false;

                                                    const isConflictDate = (
                                                        (t.tripFrequency === 'One Time' && targetDate && t.tripDate && t.tripDate.startsWith(targetDate)) ||
                                                        (t.tripFrequency === 'Recurring' && form.tripFrequency === 'Recurring' && t.recurringDays?.some(d => form.recurringDays?.includes(d))) ||
                                                        (t.tripFrequency === 'Recurring' && form.tripFrequency === 'One Time' && targetDate && t.recurringDays?.includes(new Date(targetDate).toLocaleDateString('en-US', { weekday: 'short' }))) ||
                                                        (t.tripFrequency === 'One Time' && form.tripFrequency === 'Recurring' && t.tripDate && form.recurringDays?.includes(new Date(t.tripDate).toLocaleDateString('en-US', { weekday: 'short' })))
                                                    );

                                                    if (!isConflictDate) return false;

                                                    return (t.billingType === 'Monthly' || (t.startTime && (() => {
                                                        const tsh = parseInt(t.startTime.split(':')[0]);
                                                        const teh = parseInt((t.endTime || t.startTime).split(':')[0]);
                                                        return (sh <= teh && eh >= tsh);
                                                    })()));
                                                });
                                                return conflict ? '#ef4444' : '#e2e8f0';
                                            })() }} value={form.driverId} onChange={e => {
                                                const val = e.target.value;
                                                let nextVehicleId = form.vehicleId;
                                                if (val && Array.isArray(mappings)) {
                                                    const m = mappings.find(x => (x.driverId?._id || x.driverId) === val);
                                                    if (m) {
                                                        nextVehicleId = m.vehicleId?._id || m.vehicleId || form.vehicleId;
                                                    }
                                                }
                                                setForm(prev => ({ ...prev, driverId: val, vehicleId: nextVehicleId }));
                                            }}>
                                                <option value="">Select Driver</option>
                                                {drivers.map(d => {
                                                    let isLeave = false;
                                                    if (d.leaves && d.leaves.length > 0) {
                                                        const targetTime = form.tripDate ? new Date(String(form.tripDate).split('T')[0]).getTime() : new Date().setHours(0,0,0,0);
                                                        isLeave = d.leaves.some(l => {
                                                            if (l.status !== 'Approved') return false;
                                                            const st = new Date(l.startDate).setHours(0,0,0,0);
                                                            const en = new Date(l.endDate).setHours(23,59,59,999);
                                                            return targetTime >= st && targetTime <= en;
                                                        });
                                                    }
                                                    return (
                                                        <option key={d._id} value={d._id} disabled={isLeave}>
                                                            {d.name} {d.driverCode ? `(${d.driverCode})` : ''} {isLeave ? '(On Leave)' : ''}
                                                        </option>
                                                    );
                                                })}
                                            </select>
                                            {form.driverId && (
                                                <div style={{ marginTop: 8 }}>
                                                    <div style={{ display: 'flex', gap: 2, height: 6, borderRadius: 3, overflow: 'hidden' }}>
                                                        {Array.from({ length: 24 }).map((_, h) => {
                                                            const sh = form.startTime ? parseInt(form.startTime.split(':')[0]) : -1;
                                                            const eh = (form.endTime || form.startTime) ? parseInt((form.endTime || form.startTime).split(':')[0]) : -1;
                                                            const inWindow = sh !== -1 && h >= sh && h <= eh;
                                                            const targetDate = form.tripDate ? String(form.tripDate).split('T')[0] : null;
                                                            const busy = trips.some(t => {
                                                                const isSameEntity = (t.driverId?._id === form.driverId || t.driverId === form.driverId || t.vendorProposedDriverId?._id === form.driverId || t.vendorProposedDriverId === form.driverId);
                                                                if (!isSameEntity || !['Scheduled', 'Ongoing'].includes(t.status) || t._id === form._id) return false;

                                                                const isConflictDate = (
                                                                    (t.tripFrequency === 'One Time' && targetDate && t.tripDate && t.tripDate.startsWith(targetDate)) ||
                                                                    (t.tripFrequency === 'Recurring' && form.tripFrequency === 'Recurring' && t.recurringDays?.some(d => form.recurringDays?.includes(d))) ||
                                                                    (t.tripFrequency === 'Recurring' && form.tripFrequency === 'One Time' && targetDate && t.recurringDays?.includes(new Date(targetDate).toLocaleDateString('en-US', { weekday: 'short' }))) ||
                                                                    (t.tripFrequency === 'One Time' && form.tripFrequency === 'Recurring' && t.tripDate && form.recurringDays?.includes(new Date(t.tripDate).toLocaleDateString('en-US', { weekday: 'short' })))
                                                                );

                                                                if (!isConflictDate) return false;

                                                                return (t.billingType === 'Monthly' || (t.startTime && h >= parseInt(t.startTime.split(':')[0]) && h <= parseInt((t.endTime || t.startTime).split(':')[0])));
                                                            });
                                                            return <div key={h} title={busy ? `Busy at ${h}:00` : `Available at ${h}:00`} style={{ flex: 1, background: busy ? '#ef4444' : (inWindow ? '#16539C' : '#e2e8f0'), borderTop: inWindow ? '2px solid #fff' : 'none' }} />;
                                                        })}
                                                    </div>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 3, fontSize: 8, color: '#94a3b8', fontWeight: 600 }}>
                                                        <span>12AM</span><span>6AM</span><span>12PM</span><span>6PM</span><span>11PM</span>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {!isDispatchOnly && (
                                <>
                                    <div className="md-form-group pickup-search-container" style={{ gridColumn: 'span 2', position: 'relative' }}>
                                        <label className="md-label" style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            Pickup (Autocomplete)*
                                            {form.pickupLat && <span style={{ fontSize: '10px', color: '#10b981' }}>Selected</span>}
                                        </label>
                                        <div style={{ position: 'relative' }}>
                                            <input
                                                className="md-input"
                                                value={pickupQuery}
                                                onChange={e => {
                                                    setPickupQuery(e.target.value);
                                                    if (form.pickupLat) {
                                                        setForm({ ...form, pickupLocation: e.target.value, pickupLat: null, pickupLng: null });
                                                    }
                                                }}
                                                onFocus={() => { if (pickupSuggestions.length > 0) setShowPickupSuggestions(true); }}
                                                placeholder="Search for pickup point..."
                                                style={{ paddingLeft: '32px' }}
                                            />
                                            <div style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }}>
                                                <Icon d={icons.search} size={14} />
                                            </div>
                                        </div>
                                        {showPickupSuggestions && pickupSuggestions.length > 0 && (
                                            <ul style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', zIndex: 1000, margin: 0, padding: 0, listStyle: 'none', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)', maxHeight: '200px', overflowY: 'auto' }}>
                                                {pickupSuggestions.map(place => (
                                                    <li key={place.place_id} onClick={() => handleSelectLocation(place, 'pickup')} style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9', fontSize: '12px', color: '#334155' }}>
                                                        {place.display_name}
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                    <div className="md-form-group drop-search-container" style={{ gridColumn: 'span 2', position: 'relative' }}>
                                        <label className="md-label" style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            Drop (Autocomplete)*
                                            {form.dropLat && <span style={{ fontSize: '10px', color: '#10b981' }}>Selected</span>}
                                        </label>
                                        <div style={{ position: 'relative' }}>
                                            <input
                                                className="md-input"
                                                value={dropQuery}
                                                onChange={e => {
                                                    setDropQuery(e.target.value);
                                                    if (form.dropLat) {
                                                        setForm({ ...form, dropLocation: e.target.value, dropLat: null, dropLng: null });
                                                    }
                                                }}
                                                onFocus={() => { if (dropSuggestions.length > 0) setShowDropSuggestions(true); }}
                                                placeholder="Search for destination..."
                                                style={{ paddingLeft: '32px' }}
                                            />
                                            <div style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }}>
                                                <Icon d={icons.search} size={14} />
                                            </div>
                                        </div>
                                        {showDropSuggestions && dropSuggestions.length > 0 && (
                                            <ul style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', zIndex: 1000, margin: 0, padding: 0, listStyle: 'none', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)', maxHeight: '200px', overflowY: 'auto' }}>
                                                {dropSuggestions.map(place => (
                                                    <li key={place.place_id} onClick={() => handleSelectLocation(place, 'drop')} style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9', fontSize: '12px', color: '#334155' }}>
                                                        {place.display_name}
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                </>
                            )}
                            {!isDispatchOnly && (
                                <>
                                    <div className="md-form-group">
                                        <label className="md-label">Frequency*</label>
                                        <select className="md-input" value={form.tripFrequency} onChange={e => setForm({ ...form, tripFrequency: e.target.value })}>
                                            <option value="One Time">One Time</option>
                                            <option value="Recurring">Recurring</option>
                                        </select>
                                    </div>
                                    {form.tripFrequency === 'One Time' ? (
                                        <div className="md-form-group">
                                            <label className="md-label">Trip Date*</label>
                                            <div style={{ position: 'relative' }}>
                                                <input 
                                                    type="text" 
                                                    className="md-input" 
                                                    placeholder="DD/MM/YYYY"
                                                    value={formatDateToDMY(form.tripDate)} 
                                                    onChange={e => handleDateChange('tripDate', e.target.value, e.nativeEvent)} 
                                                    style={{ paddingRight: '40px' }}
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => tripDateRef.current && tripDateRef.current.showPicker()}
                                                    style={{
                                                        position: 'absolute',
                                                        right: '12px',
                                                        top: '50%',
                                                        transform: 'translateY(-50%)',
                                                        background: 'none',
                                                        border: 'none',
                                                        cursor: 'pointer',
                                                        color: '#64748b',
                                                        padding: 0,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center'
                                                    }}
                                                >
                                                    <Icon d={icons.calendar} size={16} />
                                                </button>
                                                <input 
                                                    type="date"
                                                    ref={tripDateRef}
                                                    style={{ position: 'absolute', opacity: 0, width: 0, height: 0, pointerEvents: 'none', bottom: 0, right: 0 }}
                                                    value={form.tripDate ? String(form.tripDate).split('T')[0] : ''}
                                                    onChange={e => setForm({ ...form, tripDate: e.target.value })}
                                                />
                                            </div>
                                        </div>
                                    ) : (
                                        <>
                                            <div className="md-form-group" style={{ gridColumn: 'span 2' }}>
                                                <label className="md-label">Recurring Days</label>
                                                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                                    {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                                                        <button
                                                            key={day}
                                                            type="button"
                                                            onClick={() => toggleDay(day)}
                                                            style={{
                                                                flex: 1, minWidth: 40, padding: '8px 0', borderRadius: 8, border: '1px solid #e2e8f0',
                                                                background: form.recurringDays.includes(day) ? '#063A5C' : '#fff',
                                                                color: form.recurringDays.includes(day) ? '#fff' : '#64748b',
                                                                fontSize: 12, fontWeight: 700, cursor: 'pointer', transition: 'all 0.2s'
                                                            }}
                                                        >
                                                            {day.charAt(0)}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                            <div className="md-form-group">
                                                <label className="md-label">Effective From*</label>
                                                <div style={{ position: 'relative' }}>
                                                    <input 
                                                        type="text" 
                                                        className="md-input" 
                                                        placeholder="DD/MM/YYYY"
                                                        value={formatDateToDMY(form.startDate)} 
                                                        onChange={e => handleDateChange('startDate', e.target.value, e.nativeEvent)} 
                                                        style={{ paddingRight: '40px' }}
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => startDateRef.current && startDateRef.current.showPicker()}
                                                        style={{
                                                            position: 'absolute',
                                                            right: '12px',
                                                            top: '50%',
                                                            transform: 'translateY(-50%)',
                                                            background: 'none',
                                                            border: 'none',
                                                            cursor: 'pointer',
                                                            color: '#64748b',
                                                            padding: 0,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center'
                                                        }}
                                                    >
                                                        <Icon d={icons.calendar} size={16} />
                                                    </button>
                                                    <input 
                                                        type="date"
                                                        ref={startDateRef}
                                                        style={{ position: 'absolute', opacity: 0, width: 0, height: 0, pointerEvents: 'none', bottom: 0, right: 0 }}
                                                        value={form.startDate ? String(form.startDate).split('T')[0] : ''}
                                                        onChange={e => setForm({ ...form, startDate: e.target.value })}
                                                    />
                                                </div>
                                            </div>
                                            <div className="md-form-group">
                                                <label className="md-label">Effective To</label>
                                                <div style={{ position: 'relative' }}>
                                                    <input 
                                                        type="text" 
                                                        className="md-input" 
                                                        placeholder="DD/MM/YYYY"
                                                        value={formatDateToDMY(form.endDate)} 
                                                        onChange={e => handleDateChange('endDate', e.target.value, e.nativeEvent)} 
                                                        style={{ paddingRight: '40px' }}
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => endDateRef.current && endDateRef.current.showPicker()}
                                                        style={{
                                                            position: 'absolute',
                                                            right: '12px',
                                                            top: '50%',
                                                            transform: 'translateY(-50%)',
                                                            background: 'none',
                                                            border: 'none',
                                                            cursor: 'pointer',
                                                            color: '#64748b',
                                                            padding: 0,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center'
                                                        }}
                                                    >
                                                        <Icon d={icons.calendar} size={16} />
                                                    </button>
                                                    <input 
                                                        type="date"
                                                        ref={endDateRef}
                                                        style={{ position: 'absolute', opacity: 0, width: 0, height: 0, pointerEvents: 'none', bottom: 0, right: 0 }}
                                                        value={form.endDate ? String(form.endDate).split('T')[0] : ''}
                                                        onChange={e => setForm({ ...form, endDate: e.target.value })}
                                                    />
                                                </div>
                                            </div>
                                        </>
                                    )}
                                    <div className="md-form-group">
                                        <label className="md-label">Start Time*</label>
                                        <input type="time" className="md-input" value={form.startTime || ''} onChange={e => setForm({ ...form, startTime: e.target.value })} />
                                    </div>
                                    <div className="md-form-group">
                                        <label className="md-label">End Time*</label>
                                        <input type="time" className="md-input" value={form.endTime || ''} onChange={e => setForm({ ...form, endTime: e.target.value })} />
                                    </div>
                                    <div className="md-form-group" style={{ gridColumn: 'span 2' }}>
                                        <label className="md-label">
                                            Trip Amount / Fee {['Daily', 'Ad-hoc'].includes(form.billingType) ? '(Daily)' : '(Monthly)'}
                                        </label>
                                        <input type="number" className="md-input" placeholder="0.00" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} />
                                    </div>
                                </>
                            )}
                            {data && !isDispatchOnly && data.vehicleId && (
                                <div className="md-form-group" style={{ gridColumn: 'span 2', marginTop: 10 }}>
                                    <label className="md-label" style={{ color: '#ea580c' }}>Update Status / Change Reason*</label>
                                    <input 
                                        className="md-input" 
                                        style={{ border: '1px solid #fed7aa', background: '#fff7ed' }}
                                        placeholder="e.g. Changed driver due to shift update" 
                                        value={form.changeReason} 
                                        onChange={e => setForm({ ...form, changeReason: e.target.value })} 
                                        required
                                    />
                                </div>
                            )}
                        </div>
                        {!isDispatchOnly && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>
                                <div style={{ flex: 1, position: 'relative', borderRadius: 20, overflow: 'hidden', border: '1px solid #e2e8f0', background: '#f8fafc' }}>
                                    <div ref={mapRef} style={{ width: '100%', height: '100%' }} />
                                    {!form.routeId && !form.pickupLocation && (
                                        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(248,30,252,0.02)', backdropFilter: 'blur(2px)', color: '#94a3b8', fontSize: 13, textAlign: 'center', padding: 40 }}>
                                            <div>
                                                <Icon d={icons.map} size={32} />
                                                <p style={{ marginTop: 10 }}>Select a route or locations to preview on map</p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                {routeStops.length > 0 && (
                                    <div style={{ padding: '15px', background: '#f8fafc', borderRadius: 16, border: '1px solid #e2e8f0' }}>
                                        <div style={{ fontSize: 12, fontWeight: 700, color: '#063A5C', marginBottom: 10 }}>Route Stops ({routeStops.length})</div>
                                        <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 5 }}>
                                            {routeStops.map((s, idx) => (
                                                <div key={s._id} style={{ background: '#fff', padding: '6px 12px', borderRadius: 8, border: '1px solid #e2e8f0', whiteSpace: 'nowrap', fontSize: 11, color: '#64748b' }}>
                                                    <span style={{ fontWeight: 700, marginRight: 5 }}>{idx + 1}.</span> {s.stopName}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    <div className="md-modal-footer" style={{ borderTop: '1px solid #f1f5f9', padding: '15px 25px' }}>
                        <button className="md-cancel-btn" onClick={onClose}>Discard</button>
                        <button className="md-save-btn" onClick={handleConfirm}>Confirm Trip Schedule</button>
                    </div>
                </div>
            </div>
        </>
    );
}

function ImportWizard({ trips, operatorId, onBack, onDone }) {
    const [file, setFile] = useState(null);
    const [dragActive, setDragActive] = useState(false);
    const [parsedData, setParsedData] = useState([]);
    const [importing, setImporting] = useState(false);
    const [result, setResult] = useState(null);

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            processFile(e.dataTransfer.files[0]);
        }
    };

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            processFile(e.target.files[0]);
        }
    };

    const processFile = (selectedFile) => {
        setFile(selectedFile);
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const bstr = e.target.result;
                const wb = XLSX.read(bstr, { type: 'binary' });
                const wsname = wb.SheetNames[0];
                const ws = wb.Sheets[wsname];
                const data = XLSX.utils.sheet_to_json(ws);

                const standardized = data.map((row) => {
                    const mapped = {};
                    Object.keys(row).forEach(key => {
                        const cleanKey = key.trim();
                        mapped[cleanKey] = row[key];
                    });
                    return mapped;
                });

                setParsedData(standardized);
            } catch (err) {
                console.error(err);
                alert("Failed to parse Excel file. Please ensure it is a valid .xlsx or .xls file.");
            }
        };
        reader.readAsBinaryString(selectedFile);
    };

    const downloadTemplate = () => {
        const templateData = [
            {
                'Client': 'Infosys Ltd',
                'Route': 'IT Park Shuttle',
                'Trip Type': 'Login',
                'Frequency': 'Recurring',
                'Recurring Days': 'Mon,Tue,Wed,Thu,Fri',
                'Trip Date': '',
                'Start Time': '08:00',
                'End Time': '09:00',
                'Vehicle Plate': 'DXB-1234',
                'Driver Name': 'John Doe',
                'Amount': 500,
                'Billing Category': 'Daily',
                'Status': 'Scheduled'
            }
        ];
        const ws = XLSX.utils.json_to_sheet(templateData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Trips');
        XLSX.writeFile(wb, 'Trips_Template.xlsx');
    };

    const handleImport = async () => {
        if (parsedData.length === 0) return;
        setImporting(true);
        try {
            const mapped = parsedData.map(row => ({
                client: row['Client'],
                route: row['Route'],
                tripType: row['Trip Type'] || 'Login',
                tripFrequency: row['Frequency'] || 'One Time',
                recurringDays: row['Recurring Days'] ? String(row['Recurring Days']).split(',').map(d => d.trim()) : [],
                tripDate: row['Trip Date'],
                startTime: row['Start Time'],
                endTime: row['End Time'],
                vehiclePlate: row['Vehicle Plate'],
                driverName: row['Driver Name'],
                amount: row['Amount'],
                billingType: row['Billing Category'] || 'Daily',
                status: row['Status'] || 'Scheduled'
            }));
            const res = await axios.post(`/api/trips/import`, { operatorId, trips: mapped });
            setResult({ createdCount: res.data.successCount, updatedCount: 0, errors: res.data.errors });
        } catch (err) {
            console.error(err);
            alert(err.response?.data?.message || "Import failed. Please try again.");
        } finally {
            setImporting(false);
        }
    };

    if (result) {
        return (
            <div className="md-page" style={{ fontFamily: "'Inter', sans-serif", padding: '30px', maxWidth: '1200px', margin: '0 auto' }}>
                <div style={{ background: 'white', borderRadius: '16px', padding: '40px', textAlign: 'center', boxShadow: '0 10px 25px rgba(0,0,0,0.05)', border: '1px solid #f1f5f9' }}>
                    <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '80px', height: '80px', borderRadius: '50%', background: '#d1fae5', color: '#10b981', marginBottom: '24px' }}>
                        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                    </div>
                    <h2 style={{ fontSize: '28px', fontWeight: '800', color: '#1e293b', marginBottom: '12px' }}>Import Completed!</h2>
                    <p style={{ fontSize: '15px', color: '#64748b', maxWidth: '600px', margin: '0 auto 30px' }}>
                        The trip data has been processed.
                    </p>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px', maxWidth: '700px', margin: '0 auto 30px' }}>
                        <div style={{ background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: '12px', padding: '20px', textAlign: 'center' }}>
                            <div style={{ fontSize: '32px', fontWeight: '800', color: '#065f46' }}>{result.updatedCount || 0}</div>
                            <div style={{ fontSize: '13px', fontWeight: '600', color: '#047857', marginTop: '4px' }}>Trips Updated</div>
                        </div>
                        <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '12px', padding: '20px', textAlign: 'center' }}>
                            <div style={{ fontSize: '32px', fontWeight: '800', color: '#1e40af' }}>{result.createdCount || 0}</div>
                            <div style={{ fontSize: '13px', fontWeight: '600', color: '#1d4ed8', marginTop: '4px' }}>New Trips Created</div>
                        </div>
                        <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '12px', padding: '20px', textAlign: 'center' }}>
                            <div style={{ fontSize: '32px', fontWeight: '800', color: '#92400e' }}>{(result.errors?.length) || 0}</div>
                            <div style={{ fontSize: '13px', fontWeight: '600', color: '#b45309', marginTop: '4px' }}>Skipped / Warnings</div>
                        </div>
                    </div>
                    <button onClick={onDone} style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '12px 30px', borderRadius: '10px', border: 'none', background: 'linear-gradient(135deg,#16539C,#00B2A9)', color: 'white', fontSize: '15px', fontWeight: '700', cursor: 'pointer', boxShadow: '0 4px 12px rgba(79,70,229,0.3)', transition: 'all 0.15s' }}>
                        Return to Trips
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="md-page" style={{ fontFamily: "'Inter', sans-serif", padding: '30px', maxWidth: '1200px', margin: '0 auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '30px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <button onClick={onBack} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '36px', height: '36px', borderRadius: '10px', border: '1px solid #e2e8f0', background: 'white', color: '#64748b', cursor: 'pointer', transition: 'all 0.15s' }}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" /></svg>
                    </button>
                    <div>
                        <h1 style={{ fontSize: '24px', fontWeight: '800', color: '#1e293b', margin: 0 }}>Import Trips</h1>
                        <p style={{ fontSize: '13px', color: '#64748b', margin: '4px 0 0' }}>Bulk import new trips via Excel upload.</p>
                    </div>
                </div>
            </div>

            {parsedData.length === 0 ? (
                <div style={{ display: 'grid', gridTemplateColumns: '400px 1fr', gap: '30px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                        <div style={{ background: 'white', borderRadius: '16px', border: '1px solid #e2e8f0', padding: '24px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
                            <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#1e293b', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>ℹ️ How it works</h3>
                            <ul style={{ paddingLeft: '20px', margin: 0, display: 'flex', flexDirection: 'column', gap: '12px', fontSize: '13.5px', color: '#475569', lineHeight: '1.5' }}>
                                <li>The file must be an Excel sheet (<code>.xlsx</code> or <code>.xls</code>).</li>
                                <li>All valid rows will automatically be created as brand new trips!</li>
                            </ul>
                        </div>
                        <div style={{ background: '#f8fafc', borderRadius: '16px', border: '1px dashed #cbd5e1', padding: '20px', textAlign: 'center' }}>
                            <p style={{ fontSize: '13px', color: '#475569', margin: '0 0 12px' }}>Need a starting point? Download our pre-configured format template.</p>
                            <button onClick={downloadTemplate} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 16px', borderRadius: '8px', border: '1.5px solid #cbd5e1', background: 'white', color: '#334155', fontSize: '12.5px', fontWeight: '700', cursor: 'pointer', transition: 'all 0.15s' }}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
                                Download Sample Template
                            </button>
                        </div>
                    </div>
                    <div
                        onDragEnter={handleDrag} onDragOver={handleDrag} onDragLeave={handleDrag} onDrop={handleDrop}
                        style={{
                            background: dragActive ? '#f0f7ff' : 'white', borderRadius: '16px', border: `2px dashed ${dragActive ? '#16539C' : '#cbd5e1'}`,
                            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 40px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', transition: 'all 0.15s', cursor: 'pointer'
                        }}
                        onClick={() => document.getElementById('file-upload-input').click()}
                    >
                        <input id="file-upload-input" type="file" accept=".xlsx,.xls" onChange={handleFileChange} style={{ display: 'none' }} />
                        <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '64px', height: '64px', borderRadius: '16px', background: '#eef2ff', color: '#16539C', marginBottom: '20px' }}>
                            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></svg>
                        </div>
                        <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#1e293b', margin: '0 0 8px' }}>Drag & Drop Excel File</h3>
                        <p style={{ fontSize: '13.5px', color: '#64748b', margin: '0 0 16px' }}>or click to browse your local files</p>
                        <span style={{ fontSize: '12px', color: '#94a3b8', background: '#f8fafc', padding: '6px 12px', borderRadius: '6px', border: '1px solid #f1f5f9' }}>Supports .xlsx, .xls formats</span>
                    </div>
                </div>
            ) : (
                <div style={{ background: 'white', borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px', borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
                        <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <span style={{ fontSize: '15px', fontWeight: '700', color: '#1e293b' }}>{file?.name}</span>
                                <span style={{ fontSize: '12px', color: '#64748b', background: '#cbd5e1', padding: '2px 8px', borderRadius: '999px', fontWeight: '600' }}>{parsedData.length} Rows Found</span>
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: '12px' }}>
                            <button onClick={() => { setFile(null); setParsedData([]); }} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '9px 18px', borderRadius: '10px', border: '1.5px solid #cbd5e1', background: 'white', color: '#475569', fontSize: '13px', fontWeight: '700', cursor: 'pointer', transition: 'all 0.15s' }}>Change File</button>
                            <button onClick={handleImport} disabled={importing} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '9px 22px', borderRadius: '10px', border: 'none', background: 'linear-gradient(135deg,#16539C,#00B2A9)', color: 'white', fontSize: '13px', fontWeight: '700', cursor: 'pointer', boxShadow: '0 4px 12px rgba(79,70,229,0.3)', transition: 'all 0.15s' }}>
                                {importing ? 'Processing...' : `Proceed Import (${parsedData.length})`}
                            </button>
                        </div>
                    </div>
                    <div style={{ overflowX: 'auto', maxHeight: '500px' }}>
                        <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: '800px' }}>
                            <thead>
                                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                                    <th style={{ padding: '14px 20px', textAlign: 'left', fontSize: '11px', fontWeight: '700', color: '#475569', textTransform: 'uppercase' }}>Client</th>
                                    <th style={{ padding: '14px 20px', textAlign: 'left', fontSize: '11px', fontWeight: '700', color: '#475569', textTransform: 'uppercase' }}>Route</th>
                                    <th style={{ padding: '14px 20px', textAlign: 'left', fontSize: '11px', fontWeight: '700', color: '#475569', textTransform: 'uppercase' }}>Vehicle Plate</th>
                                    <th style={{ padding: '14px 20px', textAlign: 'left', fontSize: '11px', fontWeight: '700', color: '#475569', textTransform: 'uppercase' }}>Driver Name</th>
                                    <th style={{ padding: '14px 20px', textAlign: 'left', fontSize: '11px', fontWeight: '700', color: '#475569', textTransform: 'uppercase' }}>Start Time</th>
                                    <th style={{ padding: '14px 20px', textAlign: 'left', fontSize: '11px', fontWeight: '700', color: '#475569', textTransform: 'uppercase' }}>Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                {parsedData.map((d, i) => (
                                    <tr key={i} style={{ borderBottom: '1px solid #f1f5f9', background: '#fff' }}>
                                        <td style={{ padding: '14px 20px', fontWeight: '700', color: '#1e293b' }}>{d.Client || d['Client'] || '—'}</td>
                                        <td style={{ padding: '14px 20px', color: '#475569', fontSize: '13px' }}>{d.Route || d['Route'] || '—'}</td>
                                        <td style={{ padding: '14px 20px', color: '#475569', fontSize: '13px' }}>{d.VehiclePlate || d['Vehicle Plate'] || '—'}</td>
                                        <td style={{ padding: '14px 20px', color: '#475569', fontSize: '13px' }}>{d.DriverName || d['Driver Name'] || '—'}</td>
                                        <td style={{ padding: '14px 20px', color: '#475569', fontSize: '13px' }}>{d.StartTime || d['Start Time'] || '—'}</td>
                                        <td style={{ padding: '14px 20px', color: '#475569', fontSize: '13px' }}>{d.Amount || d['Amount'] || '—'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}

